import React, { useState, useEffect } from 'react';
import styles from './SoukBargainGame.module.css';

// Transform Dropbox share URLs to direct CDN URLs so merchant avatars load
// fast (skips the www.dropbox.com → dl.dropboxusercontent.com 302 redirect)
// and cache properly between page refreshes (no more "hide and show" flicker).
const fixDropbox = (url) => {
  if (!url || typeof url !== 'string') return url;
  if (!url.includes('dropbox.com')) return url;
  return url
    .replace('www.dropbox.com', 'dl.dropboxusercontent.com')
    .replace(/[?&]dl=[01]/, (m) => m[0] + 'raw=1');
};

const SoukBargainGame = ({ API, isDarkMode, userToken, userId }) => {
  // App-shell caches: read synchronously so recipe grid + merchants + items
  // render from frame 1 instead of "⏳ Loading recipes..." spinner on Ctrl+R.
  const _readSbCache = (key) => {
    try { const c = localStorage.getItem(key); if (c) return JSON.parse(c); } catch {}
    return null;
  };
  const _cachedSbSettings = _readSbCache('sbGameSettingsCache');
  const _cachedSbItems = _readSbCache('sbItemsCache');
  const _cachedSbMerchants = _readSbCache('sbMerchantsCache');
  const _cachedSbRecipes = _readSbCache('sbRecipesCache');
  const _cachedSbTexts = _readSbCache('sbGameTextsCache');
  const _cachedSbUserState = _readSbCache('sbUserStateCache');
  const _hasAllSbAdminCaches = !!(_cachedSbSettings && _cachedSbItems && _cachedSbMerchants && _cachedSbRecipes && _cachedSbTexts);

  // URL routing: encode current step + selections so refresh stays on the
  // same page (recipe-select, merchant-shop, checkout, etc).
  const _sbUrl = (() => {
    try {
      const p = new URLSearchParams(window.location.search);
      return {
        step: p.get('step') || null,
        cat: p.get('cat') || null,
        recipe: p.get('recipe') || null,
        merchant: p.get('merchant') || null,
      };
    } catch { return { step: null, cat: null, recipe: null, merchant: null }; }
  })();
  const _initRecipe = _sbUrl.recipe && _cachedSbRecipes
    ? _cachedSbRecipes.find(r => String(r.id) === _sbUrl.recipe) || null : null;
  const _initMerchant = _sbUrl.merchant && _cachedSbMerchants
    ? _cachedSbMerchants.find(m => String(m.id) === _sbUrl.merchant) || null : null;
  const _initCategory = _sbUrl.cat ? { id: _sbUrl.cat } : null;

  // Push current step + context to URL on every step transition.
  const _pushSbUrl = (step, extras = {}) => {
    try {
      const params = new URLSearchParams();
      if (step && step !== 'category') params.set('step', step);
      if (extras.cat) params.set('cat', extras.cat);
      if (extras.recipe) params.set('recipe', extras.recipe);
      if (extras.merchant) params.set('merchant', extras.merchant);
      const qs = params.toString();
      window.history.pushState({}, '', '/games/SoukBargainGame' + (qs ? '?' + qs : ''));
    } catch {}
  };

  // Game flow states — initial values come from URL when present, so refresh
  // returns the user to the page they were on.
  const [gameStep, setGameStep] = useState(_sbUrl.step || 'category'); // category, recipe-select, confirm-recipe, merchant-shop, checkout
  const [selectedCategory, setSelectedCategory] = useState(_initCategory);
  const [selectedRecipe, setSelectedRecipe] = useState(_initRecipe);
  const [selectedMerchant, setSelectedMerchant] = useState(_initMerchant);
  const [showSidebar, setShowSidebar] = useState(true);

  // Data states
  const [gameSettings, setGameSettings] = useState(_cachedSbSettings);
  const [items, setItems] = useState(_cachedSbItems || []);
  const [merchants, setMerchants] = useState(_cachedSbMerchants || []);
  const [recipes, setRecipes] = useState(_cachedSbRecipes || []);
  const [userState, setUserState] = useState(_cachedSbUserState);
  const [basket, setBasket] = useState([]);
  const [totalDH, setTotalDH] = useState(0);
  // Skip the loading spinner if all admin lists are cached
  const [dataLoaded, setDataLoaded] = useState(_hasAllSbAdminCaches);
  
  // Wallet states
  const [initialBudget, setInitialBudget] = useState(100); // Default 100 DH
  const [remainingMoney, setRemainingMoney] = useState(100);
  const [showBudgetWarning, setShowBudgetWarning] = useState(false);
  
  // Merchant inventory (all items available)
  const [allAvailableItems, setAllAvailableItems] = useState([]);
  
  // Dialogue/popup states
  const [showRecipeConfirm, setShowRecipeConfirm] = useState(false);
  const [showMerchantGreeting, setShowMerchantGreeting] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [gameTexts, setGameTexts] = useState(_cachedSbTexts);
  const [greetingMerchant, setGreetingMerchant] = useState(null);
  const [merchantDialogue, setMerchantDialogue] = useState(null);
  const [bargainingItem, setBargainingItem] = useState(null);
  const [userOffer, setUserOffer] = useState('');
  const [bargainResult, setBargainResult] = useState(null);
  const [wrongItemWarning, setWrongItemWarning] = useState(null);
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  const [showQuantityModal, setShowQuantityModal] = useState(false);
  const [quantityItem, setQuantityItem] = useState(null);
  const [selectedQuantity, setSelectedQuantity] = useState('');
  const [showMerchantQuiz, setShowMerchantQuiz] = useState(false);
  const [quizQuestion, setQuizQuestion] = useState(null);
  const [quizAnswer, setQuizAnswer] = useState(null);
  const [showQuizResult, setShowQuizResult] = useState(false);
  const [showPriceComparison, setShowPriceComparison] = useState(false);
  const [comparisonItem, setComparisonItem] = useState(null);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { recipeId, recipeName }
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [activeSession, setActiveSession] = useState(_readSbCache('sbActiveSessionCache')); // saved session from backend (cached so the "Unfinished Shopping Trip" banner shows from frame 1)

  // Mirror gameStep + selections to URL whenever they change. This makes
  // refresh return the user to the same page (recipe-select / merchant-shop /
  // checkout) instead of the category home.
  useEffect(() => {
    _pushSbUrl(gameStep, {
      cat: selectedCategory?.id,
      recipe: selectedRecipe?.id,
      merchant: selectedMerchant?.id,
    });
  }, [gameStep, selectedCategory, selectedRecipe, selectedMerchant]);

  // First-visit fallback: if URL had ?recipe=/?merchant= but caches were empty
  // at init, fill in selectedRecipe/selectedMerchant once recipes/merchants
  // arrive from the backend. Sync init handles the cached-data case already.
  useEffect(() => {
    if (!selectedRecipe && _sbUrl.recipe && recipes.length > 0) {
      const r = recipes.find(x => String(x.id) === _sbUrl.recipe);
      if (r) setSelectedRecipe(r);
    }
    if (!selectedMerchant && _sbUrl.merchant && merchants.length > 0) {
      const m = merchants.find(x => String(x.id) === _sbUrl.merchant);
      if (m) setSelectedMerchant(m);
    }
  }, [recipes, merchants]);

  // Mirror activeSession to localStorage so the "Unfinished Shopping Trip"
  // banner renders from frame 1 on Ctrl+R instead of popping in after fetch.
  useEffect(() => {
    try {
      if (activeSession) {
        localStorage.setItem('sbActiveSessionCache', JSON.stringify(activeSession));
      } else {
        localStorage.removeItem('sbActiveSessionCache');
      }
    } catch {}
  }, [activeSession]);

  // Auto-restore basket from activeSession when user refreshes into merchant-shop
  // and we have a saved session matching the current recipe. Mirrors the field
  // mapping in resumeSession (item_id → id, price_paid_dh → price_paid) so the
  // checkout/sidebar render doesn't crash on undefined fields.
  useEffect(() => {
    if (gameStep !== 'merchant-shop' || !activeSession || basket.length > 0) return;
    if (!selectedRecipe || activeSession.recipe_id !== selectedRecipe.id) return;
    const restoredBasket = (activeSession.items || []).map(item => ({
      ...item,
      id: item.item_id ?? item.id,
      price_paid: item.price_paid_dh ?? item.price_paid ?? 0,
    }));
    setBasket(restoredBasket);
    setTotalDH(activeSession.total_dh || 0);
    if (typeof activeSession.initial_budget === 'number') setInitialBudget(activeSession.initial_budget);
    if (typeof activeSession.remaining_money === 'number') setRemainingMoney(activeSession.remaining_money);
  }, [gameStep, activeSession, selectedRecipe]);

  // Fetch all game data
  useEffect(() => {
    // Randomize inventories when component mounts
    const randomizeAndLoad = async () => {
      try {
        await fetch(`${API}/souk/randomize-inventories`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' }
        });
      } catch (error) {
        console.error('Error randomizing inventories:', error);
      }
      loadGameData();
    };
    
    randomizeAndLoad();
  }, [API]);

  const loadGameData = async () => {
    try {
      // Load settings
      const settingsRes = await fetch(`${API}/souk/settings`);
      const settingsData = await settingsRes.json();
      setGameSettings(settingsData);
      try { localStorage.setItem('sbGameSettingsCache', JSON.stringify(settingsData)); } catch {}

      // Load items
      const itemsRes = await fetch(`${API}/souk/items`);
      const itemsData = await itemsRes.json();
      setItems(itemsData);
      try { localStorage.setItem('sbItemsCache', JSON.stringify(itemsData)); } catch {}

      // Load merchants
      const merchantsRes = await fetch(`${API}/souk/merchants`);
      const merchantsData = await merchantsRes.json();
      setMerchants(merchantsData);
      try { localStorage.setItem('sbMerchantsCache', JSON.stringify(merchantsData)); } catch {}

      // Load recipes
      const recipesRes = await fetch(`${API}/souk/recipes`);
      const recipesData = await recipesRes.json();
      setRecipes(recipesData);
      try { localStorage.setItem('sbRecipesCache', JSON.stringify(recipesData)); } catch {}

      // Load game texts
      const textsRes = await fetch(`${API}/souk/game-texts`);
      const textsData = await textsRes.json();
      setGameTexts(textsData);
      try { localStorage.setItem('sbGameTextsCache', JSON.stringify(textsData)); } catch {}

      // Load user state
      if (userId) {
        const stateRes = await fetch(`${API}/souk/state/${userId}`, {
          headers: { 'Authorization': `Bearer ${userToken}` }
        });
        const stateData = await stateRes.json();
        setUserState(stateData);
        try { localStorage.setItem('sbUserStateCache', JSON.stringify(stateData)); } catch {}

        // Check for active shopping session
        try {
          const basketRes = await fetch(`${API}/souk/basket/${userId}`, {
            headers: { 'Authorization': `Bearer ${userToken}` }
          });
          if (basketRes.ok) {
            const basketData = await basketRes.json();
            if (basketData.is_active && basketData.selected_recipe && basketData.items?.length > 0) {
              setActiveSession(basketData);
              try { localStorage.setItem('sbActiveSessionCache', JSON.stringify(basketData)); } catch {}
            } else {
              // Session is no longer active — clear stale cache
              try { localStorage.removeItem('sbActiveSessionCache'); } catch {}
            }
          }
        } catch (e) {
          console.error('Error loading session:', e);
        }
      }

      setDataLoaded(true);
    } catch (error) {
      console.error('Error loading game data:', error);
      setDataLoaded(false);
    }
  };

  // Get merchant's items with their specific prices
  // Lazy-init merchantItems from cache so refresh inside a merchant renders
  // the item grid from frame 1 instead of flashing an empty grid before fetch.
  const [merchantItems, setMerchantItems] = useState(() => {
    const id = _initMerchant?.id;
    if (!id) return [];
    try { const c = localStorage.getItem(`sbMerchantInventoryCache_${id}`); if (c) return JSON.parse(c); } catch {}
    return [];
  });

  // Load inventory when merchant is selected.
  // - If we already have cached items in state for THIS merchant (from useState
  //   init or from a previous fetch), skip the silent refresh — prevents
  //   image-position swap and avatar re-decode on Ctrl+R.
  // - On first-ever visit to a merchant: fetch + shuffle + cache.
  // - When fresh data arrives for a merchant whose cached order exists, sort
  //   the fresh data to match cached order so positions stay stable.
  useEffect(() => {
    const loadMerchantInventory = async () => {
      if (!selectedMerchant) {
        setMerchantItems([]);
        return;
      }

      // Already have cached items for this merchant in state? Skip refresh entirely.
      if (merchantItems.length > 0 && merchantItems[0]?._merchant_id === selectedMerchant.id) {
        return;
      }

      try {
        const response = await fetch(`${API}/souk/merchant-inventory/${selectedMerchant.id}`);
        const data = await response.json();

        // If we have a cached order on disk for this merchant, sort fresh data
        // to match it (so the user keeps the same item order they were viewing).
        let cachedOrder = null;
        try {
          const c = localStorage.getItem(`sbMerchantInventoryCache_${selectedMerchant.id}`);
          if (c) cachedOrder = JSON.parse(c);
        } catch {}

        let finalData;
        if (Array.isArray(cachedOrder) && cachedOrder.length === data.length) {
          const idIndex = new Map(cachedOrder.map((it, i) => [it.id, i]));
          finalData = [...data].sort((a, b) => (idIndex.get(a.id) ?? 999) - (idIndex.get(b.id) ?? 999));
        } else {
          // First-ever visit (or item count changed) → shuffle for variety
          finalData = data.slice();
          for (let i = finalData.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [finalData[i], finalData[j]] = [finalData[j], finalData[i]];
          }
        }

        // Tag with merchant id so the skip-refresh check above works on next mount
        finalData.forEach(it => { it._merchant_id = selectedMerchant.id; });

        setMerchantItems(finalData);
        try { localStorage.setItem(`sbMerchantInventoryCache_${selectedMerchant.id}`, JSON.stringify(finalData)); } catch {}
      } catch (error) {
        console.error('Error loading merchant inventory:', error);
      }
    };

    loadMerchantInventory();
  }, [selectedMerchant, API]);

  // Start shopping for recipe
  const confirmAndStartShopping = async () => {
    setShowRecipeConfirm(false);
    setGameStep('merchant-shop');
    setBasket([]);
    setTotalDH(0);
    // Initialize wallet based on recipe estimated cost
    const budget = Math.ceil(selectedRecipe.estimated_cost_dh * 1.2); // 20% buffer
    setInitialBudget(budget);
    setRemainingMoney(budget);
    setActiveSession(null);
    // Save initial session to DB
    if (userId) {
      try {
        await fetch(`${API}/souk/basket/${userId}/save-session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${userToken}` },
          body: JSON.stringify({
            items: [], total_dh: 0, recipe_id: selectedRecipe.id,
            selected_recipe: selectedRecipe, game_step: 'merchant-shop',
            initial_budget: budget, remaining_money: budget,
          })
        });
      } catch (e) { console.error('Failed to save initial session:', e); }
    }
  };

  // Save session to database for resume later
  const saveSession = async (currentBasket, currentTotal, currentRemaining) => {
    if (!userId || !selectedRecipe) return;
    try {
      await fetch(`${API}/souk/basket/${userId}/save-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${userToken}` },
        body: JSON.stringify({
          items: currentBasket.map(b => ({
            item_id: b.id,
            quantity: b.quantity || '1',
            price_paid_dh: b.price_paid,
            merchant_id: b.merchant_id,
            merchant_name: b.merchant_name,
            name_english: b.name_english,
            name_darija: b.name_darija,
            category: b.category,
            price_dh: b.price_dh || b.price_paid,
          })),
          total_dh: currentTotal,
          recipe_id: selectedRecipe.id,
          selected_recipe: selectedRecipe,
          game_step: 'merchant-shop',
          initial_budget: initialBudget,
          remaining_money: currentRemaining,
        })
      });
    } catch (e) {
      console.error('Failed to save session:', e);
    }
  };

  // Resume a saved shopping session
  const resumeSession = () => {
    if (!activeSession) return;
    setSelectedRecipe(activeSession.selected_recipe);
    const restoredBasket = (activeSession.items || []).map(item => ({
      ...item,
      id: item.item_id,
      price_paid: item.price_paid_dh,
    }));
    setBasket(restoredBasket);
    setTotalDH(activeSession.total_dh || 0);
    setInitialBudget(activeSession.initial_budget || 100);
    setRemainingMoney(activeSession.remaining_money || 100);
    setGameStep('merchant-shop');
    setActiveSession(null);
  };

  // Clear saved session and start fresh
  const clearAndReset = async () => {
    try {
      await fetch(`${API}/souk/basket/${userId}/clear`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${userToken}` }
      });
    } catch (e) { console.error(e); }
    setActiveSession(null);
  };

  const convertToUSD = (dh) => {
    if (!gameSettings) return 0;
    return (dh / gameSettings.exchange_rate).toFixed(2);
  };

  const addItemToBasket = (item, merchant, bargainedPrice = null, quantity = null) => {
    // Check if item already in basket (duplicate detection)
    const alreadyInBasket = basket.some(b => b.id === item.id);
    if (alreadyInBasket) {
      setDuplicateWarning({
        item: item.name_english,
        itemDarija: item.name_darija,
        message: `You already have ${item.name_english} (${item.name_darija}) in your shopping basket! Look for the other items you still need.`
      });
      return; // Don't add duplicate
    }
    
    // Check if item is needed for recipe
    const requiredItemIds = selectedRecipe.required_items.map(ri => ri.item_id);
    const isNeeded = requiredItemIds.includes(item.id);
    
    if (!isNeeded) {
      // Store for wrong item warning with merchant context
      setWrongItemWarning({
        item: item,
        merchant: merchant,
        bargainedPrice: bargainedPrice,
        quantity: quantity,
        message: `${item.name_english} is not needed for ${selectedRecipe.name_english}. It's a waste of money!`
      });
      return; // Don't add yet, wait for user confirmation
    }
    
    // Get required quantity
    const quantityInfo = getItemQuantity(item.id);
    const itemQuantity = quantity || (quantityInfo ? `${quantityInfo.quantity} ${quantityInfo.unit}` : '1 unit');
    
    const finalPrice = bargainedPrice || item.price_dh;
    
    // Check if user has enough money
    const newTotal = totalDH + finalPrice;
    if (newTotal > initialBudget) {
      setShowBudgetWarning({
        item: item,
        merchant: merchant,
        price: finalPrice,
        needed: newTotal,
        available: initialBudget,
        shortfall: newTotal - initialBudget,
        message: `Not enough money! ${item.name_english} costs ${finalPrice.toFixed(2)} DH, but you only have ${(initialBudget - totalDH).toFixed(2)} DH left in your wallet.`
      });
      return; // Don't add item
    }
    
    const newBasket = [...basket, { 
      ...item, 
      merchant_name: merchant.name, 
      merchant_id: merchant.id, 
      price_paid: finalPrice,
      quantity: itemQuantity
    }];
    setBasket(newBasket);
    
    // Calculate total and update wallet
    const total = newBasket.reduce((sum, item) => sum + item.price_paid, 0);
    setTotalDH(total);
    setRemainingMoney(initialBudget - total);
    saveSession(newBasket, total, initialBudget - total);

    // Show merchant acknowledgment
    if (isNeeded) {
      const qtyText = quantityInfo ? `${quantityInfo.quantity} ${quantityInfo.unit}` : '';
      setMerchantDialogue({
        merchant: merchant.name,
        avatar_url: merchant.avatar_url,
        message: (gameTexts?.merchant_welcome || 'Welcome! Take {qty} of {item} you need for {recipe}. Good choice!').replace('{qty}', qtyText).replace('{item}', item.name_darija).replace('{recipe}', selectedRecipe.name_darija),
        icon: merchant.personality === 'helpful' ? '😊' : merchant.personality === 'generous' ? '🤗' : '😄'
      });
      
      // Check if more items needed
      setTimeout(() => {
        checkMerchantDialogue(merchant, item);
      }, 2500);
    }
  };

  const checkMerchantDialogue = async (merchant, addedItem) => {
    if (!selectedRecipe) return;

    // Get all required items
    const requiredItemIds = selectedRecipe.required_items.map(ri => ri.item_id);
    const requiredItems = items.filter(i => requiredItemIds.includes(i.id));
    
    // Get items user still needs
    const basketItemIds = basket.map(b => b.id);
    const missingItems = requiredItems.filter(item => !basketItemIds.includes(item.id) && item.id !== addedItem.id);

    if (missingItems.length > 0) {
      // Find which merchant sells a missing item (by category)
      let merchantWithMissing = null;
      let missingItemName = '';
      let missingItemDarija = '';
      
      for (const missingItem of missingItems) {
        // Find merchants that sell this category
        const matchingMerchants = merchants.filter(m => m.category === missingItem.category);
        if (matchingMerchants.length > 0) {
          // Suggest a different merchant than current one
          merchantWithMissing = matchingMerchants.find(m => m.id !== merchant.id) || matchingMerchants[0];
          missingItemName = missingItem.name_english;
          missingItemDarija = missingItem.name_darija;
          break;
        }
      }

      if (merchantWithMissing) {
        const personalityIcon = merchant.personality === 'helpful' ? '😊' : merchant.personality === 'rude' ? '😠' : merchant.personality === 'generous' ? '🤗' : merchant.personality === 'comedic' ? '😄' : '😐';
        setMerchantDialogue({
          merchant: merchant.name,
          avatar_url: merchant.avatar_url,
          message: (gameTexts?.merchant_redirect || "I don't have {item} ({item_darija}) here. Check with {other_merchant} - they may have it, maybe at a better price too!").replace('{item}', missingItemName).replace('{item_darija}', missingItemDarija).replace('{other_merchant}', merchantWithMissing.name),
          icon: personalityIcon
        });
      }
    } else {
      setMerchantDialogue({
        merchant: merchant.name,
        avatar_url: merchant.avatar_url,
        message: (gameTexts?.merchant_all_collected || 'Perfect! You have everything for {recipe}. Go to checkout now!').replace('{recipe}', selectedRecipe.name_darija),
        icon: '🎉'
      });
    }
  };

  const selectRecipeAndStart = (recipe) => {
    setSelectedRecipe(recipe);
    setShowRecipeConfirm(true);
  };

  const getMissingItems = () => {
    if (!selectedRecipe) return [];
    const requiredItemIds = selectedRecipe.required_items.map(ri => ri.item_id);
    const basketItemIds = basket.map(b => b.id);
    return items.filter(item => requiredItemIds.includes(item.id) && !basketItemIds.includes(item.id));
  };

  const getItemQuantity = (itemId) => {
    if (!selectedRecipe) return null;
    // Handle both old format (string IDs) and new format (objects with item_id)
    const reqItem = selectedRecipe.required_items.find(ri => {
      if (typeof ri === 'object' && ri.item_id) {
        return ri.item_id === itemId;
      }
      return ri === itemId;
    });
    
    if (typeof reqItem === 'object' && reqItem.item_id) {
      return { quantity: reqItem.quantity, unit: reqItem.unit };
    }
    return null;
  };

  const getRequiredItemIds = () => {
    if (!selectedRecipe || !selectedRecipe.required_items) return [];
    return selectedRecipe.required_items.map(ri => {
      if (typeof ri === 'object' && ri.item_id) {
        return ri.item_id;
      }
      return ri;
    });
  };

  // Generate merchant quiz question
  const generateMerchantQuiz = (merchant) => {
    if (!selectedRecipe) return;
    
    // Get items needed from this merchant's category
    const requiredItemIds = selectedRecipe.required_items.map(ri => ri.item_id);
    const neededItems = items.filter(item => 
      requiredItemIds.includes(item.id) && 
      item.category === merchant.category &&
      !basket.some(b => b.id === item.id)
    );
    
    if (neededItems.length === 0) {
      // No items needed from this merchant
      setMerchantDialogue({
        merchant: merchant.name,
        avatar_url: merchant.avatar_url,
        message: gameTexts?.merchant_no_items || "I'm sorry, I don't have what you're looking for. Check with other merchants!",
        icon: '😔'
      });
      return;
    }
    
    // Pick a random needed item
    const randomItem = neededItems[Math.floor(Math.random() * neededItems.length)];
    const quantityInfo = getItemQuantity(randomItem.id);
    
    if (!quantityInfo) return;
    
    // Generate 3 options (correct + 2 wrong)
    const correctQty = quantityInfo.quantity;
    const options = [
      correctQty,
      correctQty === 1 ? 2 : correctQty * 0.5,
      correctQty === 0.5 ? 1 : correctQty * 2
    ].sort(() => Math.random() - 0.5);
    
    setQuizQuestion({
      merchant: merchant,
      item: randomItem,
      correctQuantity: correctQty,
      unit: quantityInfo.unit,
      options: options
    });
    setShowMerchantQuiz(true);
  };

  // Handle quiz answer
  const handleQuizAnswer = (selectedQty) => {
    setQuizAnswer(selectedQty);
    const isCorrect = selectedQty === quizQuestion.correctQuantity;
    setShowQuizResult(true);
    
    if (!isCorrect) {
      // Wrong answer - show hint
      setTimeout(() => {
        setShowQuizResult(false);
        setShowMerchantQuiz(false);
        setQuizQuestion(null);
        setQuizAnswer(null);
      }, 3000);
    } else {
      // Correct answer - close after delay
      setTimeout(() => {
        setShowQuizResult(false);
        setShowMerchantQuiz(false);
        setQuizQuestion(null);
        setQuizAnswer(null);
      }, 2000);
    }
  };

  // Get price comparison across all merchants
  const getPriceComparison = async (item) => {
    try {
      const allPrices = [];
      
      // Get prices from all merchants of same category
      const sameCategoryMerchants = merchants.filter(m => m.category === item.category);
      
      for (const merchant of sameCategoryMerchants) {
        const response = await fetch(`${API}/souk/merchant-inventory/${merchant.id}`);
        const inventory = await response.json();
        const merchantItem = inventory.find(inv => inv.id === item.id);
        
        if (merchantItem) {
          allPrices.push({
            merchant: merchant.name,
            price: merchantItem.price_dh,
            personality: merchant.personality
          });
        }
      }
      
      setComparisonItem({
        item: item,
        prices: allPrices
      });
      setShowPriceComparison(true);
    } catch (error) {
      console.error('Error getting price comparison:', error);
    }
  };

  const submitBargain = () => {
    const originalPrice = bargainingItem.item.price_dh;
    const offer = parseFloat(userOffer);
    
    // Calculate minimum acceptable price (60% of original)
    const minAcceptable = originalPrice * 0.6;
    
    if (offer < minAcceptable) {
      // Merchant rejects
      setBargainResult({
        accepted: false,
        message: `${bargainingItem.merchant.name} says: "Sorry, I can't accept this price. Too low!"`,
        minPrice: minAcceptable,
        icon: bargainingItem.merchant.personality === 'rude' ? '😠' : '😕'
      });
    } else {
      // Merchant accepts
      const savedAmount = originalPrice - offer;
      const savedPercent = ((savedAmount / originalPrice) * 100).toFixed(0);
      
      setBargainResult({
        accepted: true,
        finalPrice: offer,
        savedAmount: savedAmount,
        savedPercent: savedPercent,
        message: `${bargainingItem.merchant.name} says: "Deal! You saved ${savedPercent}%!"`,
        icon: '🤝'
      });
      
      // Add to basket after 2 seconds
      setTimeout(() => {
        addItemToBasket(bargainingItem.item, bargainingItem.merchant, offer);
        setBargainingItem(null);
        setBargainResult(null);
      }, 2000);
    }
  };

  // Success modal state
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [cookingResult, setCookingResult] = useState(null);
  const [showShoppingAnalysis, setShowShoppingAnalysis] = useState(false);
  const [shoppingAnalysis, setShoppingAnalysis] = useState(null);

  const startCooking = async () => {
    // Calculate total savings if any items were bargained
    const totalPaid = basket.reduce((sum, item) => sum + item.price_paid, 0);
    const estimatedCost = selectedRecipe.estimated_cost_dh;
    const savings = estimatedCost - totalPaid;
    
    setCookingResult({
      recipeName: selectedRecipe.name_english,
      recipeDarija: selectedRecipe.name_darija,
      totalSpent: totalPaid,
      savings: savings > 0 ? savings : 0,
      itemsCount: basket.length
    });
    
    setShowSuccessModal(true);
    
    // Update user state if userId exists
    if (userId) {
      try {
        const res = await fetch(`${API}/souk/complete-recipe`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user_id: userId,
            recipe_id: selectedRecipe.id,
            total_spent: totalPaid,
            total_saved: savings > 0 ? savings : 0
          })
        });
        if (res.ok) {
          // Refresh user state from backend to get updated recipes_completed
          const stateRes = await fetch(`${API}/souk/state/${userId}`, {
            headers: { 'Authorization': `Bearer ${userToken}` }
          });
          if (stateRes.ok) {
            const stateData = await stateRes.json();
            setUserState(stateData);
          }
        } else {
          console.error('Complete recipe failed:', res.status, await res.text());
        }
      } catch (error) {
        console.error('Error updating game state:', error);
      }
    }
  };

  // Analyze shopping decisions
  const analyzeShoppingChoices = async () => {
    try {
      const analysis = [];
      
      // For each item in basket, check prices across all merchants
      for (const basketItem of basket) {
        const sameCategoryMerchants = merchants.filter(m => m.category === basketItem.category);
        const allPrices = [];
        
        // Get prices from all merchants
        for (const merchant of sameCategoryMerchants) {
          const response = await fetch(`${API}/souk/merchant-inventory/${merchant.id}`);
          const inventory = await response.json();
          const merchantItem = inventory.find(inv => inv.id === basketItem.id);
          
          if (merchantItem) {
            allPrices.push({
              merchant: merchant.name,
              merchantId: merchant.id,
              price: merchantItem.price_dh
            });
          }
        }
        
        // Sort by price
        allPrices.sort((a, b) => a.price - b.price);
        const cheapestPrice = allPrices[0]?.price || basketItem.price_paid;
        const yourPrice = basketItem.price_paid;
        const savings = yourPrice - cheapestPrice;
        
        let verdict = '';
        let color = 'blue';
        
        if (savings === 0) {
          verdict = '🎉 Excellent! You got the best price in the market!';
          color = 'green';
        } else if (savings > 0 && savings < basketItem.price_paid * 0.1) {
          verdict = '👍 Good choice! Very close to the best price.';
          color = 'green';
        } else if (savings > 0) {
          const cheapestMerchant = allPrices[0].merchant;
          verdict = `💡 You could save ${savings.toFixed(1)} DH if you bought from ${cheapestMerchant}`;
          color = 'orange';
        }
        
        analysis.push({
          item: basketItem,
          yourPrice: yourPrice,
          cheapestPrice: cheapestPrice,
          cheapestMerchant: allPrices[0]?.merchant,
          allPrices: allPrices,
          savings: savings,
          verdict: verdict,
          color: color,
          isNeeded: selectedRecipe.required_items.map(ri => ri.item_id).includes(basketItem.id)
        });
      }
      
      // Calculate total potential savings
      const totalSavings = analysis.reduce((sum, item) => sum + (item.savings > 0 ? item.savings : 0), 0);
      
      setShoppingAnalysis({
        items: analysis,
        totalSavings: totalSavings,
        totalSpent: basket.reduce((sum, item) => sum + item.price_paid, 0)
      });
      
      setShowShoppingAnalysis(true);
    } catch (error) {
      console.error('Error analyzing shopping:', error);
    }
  };

  const resetGame = () => {
    setShowSuccessModal(false);
    setShowCookingInstructions(false);
    setCookingResult(null);
    setGameStep('category');
    setSelectedRecipe(null);
    setSelectedMerchant(null);
    setBasket([]);
    setTotalDH(0);
    
    // Randomize inventories for new game
    fetch(`${API}/souk/randomize-inventories`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }
    }).then(() => {
      loadGameData();
    }).catch(error => {
      console.error('Error randomizing:', error);
      loadGameData();
    });
  };

  // Cooking instructions modal
  const [showCookingInstructions, setShowCookingInstructions] = useState(false);

  // Helper: get category icon for items
  const getCategoryIcon = (category) => {
    if (category === 'Vegetable') return '🥕';
    if (category === 'Meat') return '🍖';
    if (category === 'Spice') return '🌶️';
    return '🛍️';
  };

  // Helper: get category color
  const getCategoryColor = (category) => {
    if (category === 'Vegetable') return { bg: 'bg-green-100', text: 'text-green-800', dark: 'bg-green-900 text-green-200', gradient: 'from-green-500 to-emerald-600' };
    if (category === 'Meat') return { bg: 'bg-red-100', text: 'text-red-800', dark: 'bg-red-900 text-red-200', gradient: 'from-red-500 to-rose-600' };
    if (category === 'Spice') return { bg: 'bg-amber-100', text: 'text-amber-800', dark: 'bg-amber-900 text-amber-200', gradient: 'from-amber-500 to-orange-600' };
    return { bg: 'bg-blue-100', text: 'text-blue-800', dark: 'bg-blue-900 text-blue-200', gradient: 'from-blue-500 to-indigo-600' };
  };

  // Helper: personality config
  const getPersonality = (personality) => {
    const map = {
      helpful: { icon: '😊', color: 'bg-green-100 text-green-700', darkColor: 'bg-green-900 text-green-300' },
      rude: { icon: '😠', color: 'bg-red-100 text-red-700', darkColor: 'bg-red-900 text-red-300' },
      generous: { icon: '🤗', color: 'bg-yellow-100 text-yellow-700', darkColor: 'bg-yellow-900 text-yellow-300' },
      comedic: { icon: '😄', color: 'bg-purple-100 text-purple-700', darkColor: 'bg-purple-900 text-purple-300' },
    };
    return map[personality] || { icon: '😐', color: 'bg-gray-100 text-gray-700', darkColor: 'bg-gray-800 text-gray-300' };
  };

  // Group merchants by category
  const merchantsByCategory = merchants.reduce((acc, m) => {
    const cat = m.category || 'Other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(m);
    return acc;
  }, {});

  const categoryLabels = {
    Vegetable: gameTexts?.category_vegetable || 'Khedars (Vegetable Sellers)',
    Meat: gameTexts?.category_meat || 'Gezars (Meat Sellers)',
    Spice: gameTexts?.category_spice || '3etars (Spice Sellers)',
    Shop: gameTexts?.category_shop || 'Hanoots (General Shops)'
  };

  return (
    <div className={`${gameStep === 'category' ? '' : 'min-h-screen'} p-4 md:p-6 ${isDarkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-800'}`}>

      {/* ===== STICKY TOP BAR (during shopping) ===== */}
      {gameStep === 'merchant-shop' && (
        <div className={`${styles.stickyBar} p-3 md:p-4 ${isDarkMode ? 'bg-gray-800/90 border border-gray-700' : 'bg-white/90 border border-gray-200'}`}>
          <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-6">
            {/* Wallet — full width on mobile so label and amount don't overlap */}
            <div className="flex items-center gap-2 w-full md:flex-1 min-w-0">
              <span className="text-xl flex-shrink-0">💰</span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-medium opacity-70 flex-shrink-0">{gameTexts?.sticky_wallet || 'Wallet'}</span>
                  <span className={`text-sm font-black flex-shrink-0 ${
                    remainingMoney < initialBudget * 0.2 ? 'text-red-500 animate-pulse'
                    : remainingMoney < initialBudget * 0.5 ? 'text-orange-500'
                    : 'text-green-600'
                  }`}>
                    {remainingMoney.toFixed(1)} DH
                  </span>
                </div>
                <div className={styles.progressBar}>
                  <div
                    className={`${styles.progressFill} ${
                      remainingMoney < initialBudget * 0.2 ? 'bg-red-500'
                      : remainingMoney < initialBudget * 0.5 ? 'bg-orange-500'
                      : 'bg-green-500'
                    }`}
                    style={{ width: `${Math.max(0, (remainingMoney / initialBudget) * 100)}%` }}
                  />
                </div>
              </div>
            </div>

            {/* Basket + Checkout + Reset row */}
            <div className="flex items-center gap-2 justify-between md:justify-start md:gap-4 w-full md:w-auto">
              {/* Basket */}
              <div className="flex items-center gap-2 flex-shrink-0">
                <div className="relative">
                  <span className="text-xl">🧺</span>
                  {basket.length > 0 && (
                    <span className="absolute -top-1 -right-2 bg-red-500 text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">
                      {basket.length}
                    </span>
                  )}
                </div>
                <div className="text-right">
                  <p className="text-sm font-bold text-green-600 leading-tight">{totalDH.toFixed(1)} DH</p>
                  <p className="text-xs opacity-60 leading-tight">{(gameTexts?.sticky_items_left || '{count} left').replace('{count}', getMissingItems().length)}</p>
                </div>
              </div>

              {/* Checkout */}
              <button
                onClick={() => setGameStep('checkout')}
                className="px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg font-bold text-sm hover:shadow-lg transition-all flex-shrink-0"
              >
                {gameTexts?.sticky_checkout || 'Checkout'}
              </button>

              {/* Reset */}
              <button
                onClick={resetGame}
                className="px-3 py-2 text-red-500 hover:bg-red-50 rounded-lg text-sm font-medium transition-all flex-shrink-0"
                title={gameTexts?.sticky_reset || 'Reset'}
              >
                {gameTexts?.sticky_reset || 'Reset'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Non-shopping header */}
      {gameStep !== 'category' && gameStep !== 'merchant-shop' && (
        <div className="mb-4 flex justify-between items-center">
          <h1 className="text-xl md:text-2xl font-bold">🇲🇦 {gameTexts?.landing_title || 'Darija Bargain Souk'}</h1>
          <button
            onClick={resetGame}
            className="px-3 py-2 text-sm text-red-500 hover:bg-red-50 rounded-lg font-medium transition-all"
          >
            Reset
          </button>
        </div>
      )}

      {/* ===== STEP 1: FOOD LANDING PAGE ===== */}
      {gameStep === 'category' && (
        <div className="max-w-3xl mx-auto">
          <div className={`${styles.landingHero} ${isDarkMode ? styles.landingHeroDark : ''}`}>
            <div className={styles.landingPattern} />
            <div className="relative z-10">
              <div className={`${styles.landingIcon} mb-4`}>
                {gameTexts?.landing_icon ? (
                  <img src={`${API.replace('/api', '')}${gameTexts.landing_icon}`} alt="" className="w-20 h-20 md:w-24 md:h-24 object-contain mx-auto" />
                ) : (
                  <span className="text-7xl md:text-8xl">🍲</span>
                )}
              </div>
              <h1 className={styles.landingTitle}>{gameTexts?.landing_title || 'Darija Bargain Souk'}</h1>

              {/* Recipes cooked counter — always visible */}
              <div className="mt-2 mb-1">
                <span className="inline-block px-4 py-1 bg-amber-900/20 backdrop-blur-sm rounded-full text-white/90 text-sm font-medium">
                  {userState && userState.recipes_completed && userState.recipes_completed.length > 0
                    ? `🍳 ${(gameTexts?.landing_cooked || "You've cooked {count} recipe(s) so far!").replace('{count}', userState.recipes_completed.length)}`
                    : `🍳 ${gameTexts?.landing_cooked_zero || "You haven't cooked any recipes yet!"}`
                  }
                </span>
              </div>

              <p className={styles.landingSubtitle}>
                {gameTexts?.landing_subtitle || 'Learn Moroccan Darija while shopping for authentic recipes'}
              </p>
              <p className="text-white/70 text-sm mt-2 max-w-md mx-auto">
                {gameTexts?.landing_description || 'Pick a recipe, visit the souk, bargain with merchants, and cook a real Moroccan dish!'}
              </p>
              <button
                onClick={() => setGameStep('recipe-select')}
                className={`${styles.landingCta} ${isDarkMode ? styles.landingCtaDark : ''}`}
              >
                {gameTexts?.landing_button || 'Start Shopping'} 🛒
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== STEP 2: RECIPE SELECTION ===== */}
      {gameStep === 'recipe-select' && (
        <div className="max-w-5xl mx-auto">
          {/* Navigation Bar */}
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={() => setGameStep('category')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium ${isDarkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-white hover:bg-gray-100 shadow-sm'} transition-all`}
            >
              ← {gameTexts?.recipe_select_back || 'Back'}
            </button>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowProgressModal(true)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                  isDarkMode ? 'bg-green-900/50 hover:bg-green-800 border border-green-700' : 'bg-green-50 hover:bg-green-100 border border-green-200'
                }`}
              >
                <span>🏆</span>
                <span className={`font-medium text-sm ${isDarkMode ? 'text-green-300' : 'text-green-700'}`}>{gameTexts?.recipe_select_my_progress || 'My Progress'}</span>
              </button>
              <button
                onClick={() => setShowHelpModal(true)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                  isDarkMode ? 'bg-blue-900/50 hover:bg-blue-800 border border-blue-700' : 'bg-blue-50 hover:bg-blue-100 border border-blue-200'
                }`}
              >
                <span>📖</span>
                <span className={`font-medium text-sm ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>{gameTexts?.recipe_select_how_to_play || 'How to Play'}</span>
              </button>
            </div>
          </div>

          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-bold mb-2">{gameTexts?.recipe_select_title || 'What do you want to cook?'}</h2>
            <p className="opacity-60 text-sm">{gameTexts?.recipe_select_subtitle || 'Pick a Moroccan recipe and go shopping in the souk'}</p>
          </div>

          {!dataLoaded && (
            <div className="text-center py-16">
              <div className="animate-spin text-5xl mb-4">⏳</div>
              <p className="text-lg font-bold">{gameTexts?.recipe_select_loading || 'Loading recipes...'}</p>
            </div>
          )}

          {/* Resume banner for active session */}
          {activeSession && (
            <div className={`mb-6 p-4 md:p-5 rounded-2xl border-2 ${isDarkMode ? 'bg-amber-900/20 border-amber-600/50' : 'bg-amber-50 border-amber-300'}`}>
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="text-3xl">🛒</div>
                  <div>
                    <p className={`font-bold text-lg ${isDarkMode ? 'text-amber-200' : 'text-amber-800'}`}>
                      Unfinished Shopping Trip!
                    </p>
                    <p className={`text-sm ${isDarkMode ? 'text-amber-300/70' : 'text-amber-700'}`}>
                      <span className="font-medium">{activeSession.selected_recipe?.name_english}</span>
                      {' — '}{activeSession.items?.length || 0} item{(activeSession.items?.length || 0) !== 1 ? 's' : ''} in basket, {(activeSession.remaining_money || 0).toFixed(1)} DH remaining
                    </p>
                  </div>
                </div>
                <div className="flex gap-2 w-full sm:w-auto">
                  <button
                    onClick={resumeSession}
                    className="flex-1 sm:flex-none px-5 py-2.5 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl font-bold hover:shadow-lg transition-all text-sm"
                  >
                    Continue Shopping
                  </button>
                  <button
                    onClick={clearAndReset}
                    className={`flex-1 sm:flex-none px-5 py-2.5 rounded-xl font-medium text-sm transition-all ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'}`}
                  >
                    Start Fresh
                  </button>
                </div>
              </div>
            </div>
          )}

          {dataLoaded && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {recipes.map((recipe, idx) => {
                const diffColor = recipe.difficulty === 'Easy' ? 'bg-green-500' : recipe.difficulty === 'Medium' ? 'bg-yellow-500' : 'bg-red-500';
                return (
                  <div
                    key={recipe.id}
                    onClick={() => selectRecipeAndStart(recipe)}
                    className={`${styles.recipeCard} ${isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'} shadow-md`}
                    style={{ animation: 'none' }}
                  >
                    {/* Image */}
                    <div className={styles.recipeImageWrap}>
                      {recipe.image_url ? (
                        <img src={recipe.image_url} alt={recipe.name_english} width="120" height="120" loading="eager" decoding="async" />
                      ) : (
                        <div className={`${styles.recipeImagePlaceholder} ${isDarkMode ? 'bg-gradient-to-br from-gray-700 to-gray-600' : 'bg-gradient-to-br from-orange-100 to-red-100'}`}>
                          🍲
                        </div>
                      )}
                      <span className={`${styles.recipeDifficultyBadge} ${diffColor} text-white`}>
                        {recipe.difficulty}
                      </span>
                    </div>

                    {/* Info */}
                    <div className="p-4">
                      <h3 className="text-lg font-bold mb-1 leading-tight">{recipe.name_english}</h3>
                      <p className="text-sm text-blue-500 font-medium mb-2">{recipe.name_darija}</p>
                      <p className={`text-xs mb-3 line-clamp-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{recipe.description}</p>

                      <div className="flex items-center gap-3 text-xs mb-3">
                        <span className={`flex items-center gap-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          ⏱️ {recipe.cooking_time_minutes}min
                        </span>
                        <span className={`flex items-center gap-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          🍽️ {recipe.serves} servings
                        </span>
                      </div>

                      <div className={`flex items-center justify-between pt-3 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}>
                        <span className="text-green-600 font-bold text-sm">{recipe.estimated_cost_dh} DH</span>
                        <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>≈ ${convertToUSD(recipe.estimated_cost_dh)}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ===== STEP 3: MERCHANT SHOPPING ===== */}
      {gameStep === 'merchant-shop' && (
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Sidebar - Shopping List */}
          {showSidebar && selectedRecipe && (() => {
            const requiredIds = selectedRecipe.required_items?.map(ri => ri.item_id) || [];
            const requiredItems = items.filter(i => requiredIds.includes(i.id));
            const collected = basket.filter(b => requiredIds.includes(b.id)).length;
            const total = selectedRecipe.required_items?.length || 0;
            const pct = total > 0 ? Math.round((collected / total) * 100) : 0;
            const categories = [
              { key: 'Vegetable', icon: '🥕', label: gameTexts?.shopping_list_vegetables || 'VEGETABLES', color: 'emerald' },
              { key: 'Meat', icon: '🍖', label: gameTexts?.shopping_list_meat || 'MEAT', color: 'red' },
              { key: 'Spice', icon: '🌶️', label: gameTexts?.shopping_list_spices || 'SPICES', color: 'amber' },
              { key: 'Shop', icon: '🏪', label: gameTexts?.shopping_list_shop || 'SHOP ITEMS', color: 'blue' },
            ];
            return (
            <div className={`w-full lg:w-80 flex-shrink-0 rounded-2xl shadow-xl lg:sticky lg:top-16 h-fit order-2 lg:order-1 overflow-hidden ${isDarkMode ? 'bg-gray-800/95 border border-gray-700/50' : 'bg-white border border-gray-100'}`}>
              {/* Header with gradient */}
              <div className={`px-4 py-3 ${isDarkMode ? 'bg-gradient-to-r from-purple-900/80 to-indigo-900/80' : 'bg-gradient-to-r from-purple-500 to-indigo-500'}`}>
                <div className="flex items-center justify-between">
                  <h3 className="font-bold text-sm text-white flex items-center gap-2">
                    📋 {gameTexts?.shopping_list_title || 'Shopping List'}
                  </h3>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-white/80 font-medium bg-white/20 px-2 py-0.5 rounded-full">
                      {collected}/{total}
                    </span>
                    <button onClick={() => setShowSidebar(false)} className="text-white/60 hover:text-white text-lg leading-none transition-colors">&times;</button>
                  </div>
                </div>
                {/* Progress bar */}
                <div className="mt-2 h-2 rounded-full bg-white/20 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-green-400 to-emerald-300 transition-all duration-500"
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <p className="text-xs text-white/60 mt-1">{pct}% complete</p>
              </div>

              {(!dataLoaded || items.length === 0) && (
                <div className="text-center py-8 px-4">
                  <div className="animate-spin text-3xl mb-2">⏳</div>
                  <p className="text-xs opacity-70">Loading items...</p>
                </div>
              )}

              {/* Recipe name */}
              <div className={`mx-3 mt-3 px-3 py-2 rounded-xl ${isDarkMode ? 'bg-purple-900/30 border border-purple-800/30' : 'bg-purple-50 border border-purple-100'}`}>
                <p className="font-bold text-sm">{selectedRecipe.name_english}</p>
                <p className="text-xs text-purple-500">{selectedRecipe.name_darija}</p>
              </div>

              {/* Items by category */}
              <div className="px-3 py-2 space-y-1 max-h-[55vh] overflow-y-auto" style={{ scrollbarWidth: 'thin' }}>
                {categories.map(cat => {
                  const catItems = requiredItems.filter(i => i.category === cat.key);
                  if (catItems.length === 0) return null;
                  return (
                    <div key={cat.key}>
                      <p className={`text-xs font-bold py-1.5 flex items-center gap-1.5 sticky top-0 ${isDarkMode ? 'text-gray-300 bg-gray-800/95' : 'text-gray-600 bg-white'}`} style={{ zIndex: 1 }}>
                        <span>{cat.icon}</span> {cat.label}
                        <span className={`ml-auto text-xs font-normal px-1.5 py-0.5 rounded-full ${isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>
                          {catItems.filter(i => basket.some(b => b.id === i.id)).length}/{catItems.length}
                        </span>
                      </p>
                      {catItems.map(item => {
                        const inBasket = basket.some(b => b.id === item.id);
                        const quantityInfo = getItemQuantity(item.id);
                        const price = item.base_price_tokens || 0;
                        return (
                          <div key={item.id} className={`flex items-center gap-2.5 px-2.5 py-2 rounded-xl mb-1 transition-all duration-200 ${
                            inBasket
                              ? isDarkMode ? 'bg-green-900/40 border border-green-700/40' : 'bg-green-50 border border-green-200'
                              : isDarkMode ? 'bg-gray-700/50 hover:bg-gray-700' : 'bg-gray-50 hover:bg-gray-100 border border-transparent'
                          }`}>
                            <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-sm ${
                              inBasket
                                ? 'bg-green-500 text-white'
                                : isDarkMode ? 'bg-gray-600 text-gray-400' : 'bg-gray-200 text-gray-400'
                            }`}>
                              {inBasket ? '✓' : '○'}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm font-medium leading-tight ${inBasket ? 'line-through opacity-60' : ''}`}>{item.name_english}</p>
                              <p className={`text-xs leading-tight ${isDarkMode ? 'text-blue-400' : 'text-blue-500'}`}>{item.name_darija}</p>
                              {quantityInfo && (
                                <p className={`text-xs font-medium mt-0.5 ${isDarkMode ? 'text-sky-400' : 'text-sky-600'}`}>
                                  {quantityInfo.quantity} {quantityInfo.unit}
                                </p>
                              )}
                            </div>
                            <div className="text-right flex-shrink-0">
                              <p className={`text-xs font-bold ${inBasket ? 'text-green-500' : isDarkMode ? 'text-orange-400' : 'text-orange-600'}`}>
                                {price} DH
                              </p>
                              <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                ~${convertToUSD(price)}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>

              {/* Bottom Summary */}
              <div className={`px-4 py-3 ${isDarkMode ? 'bg-gray-900/60 border-t border-gray-700/50' : 'bg-gray-50 border-t border-gray-100'}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {collected === total ? '🎉 All items collected!' : `${total - collected} item${total - collected !== 1 ? 's' : ''} remaining`}
                  </span>
                </div>
                <div className={`p-3 rounded-xl text-center ${
                  isDarkMode ? 'bg-gradient-to-br from-green-900/60 to-emerald-900/60 border border-green-800/30' : 'bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200'
                }`}>
                  <p className={`text-xs font-medium mb-0.5 ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>Total Spent</p>
                  <p className="text-2xl font-black text-green-500">{totalDH.toFixed(2)} DH</p>
                  <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>≈ ${convertToUSD(totalDH)} USD</p>
                </div>
              </div>
            </div>
            );
          })()}

          {/* Main Shopping Area */}
          <div className="flex-1 order-1 lg:order-2">
            {!showSidebar && (
              <button onClick={() => setShowSidebar(true)} className={`mb-4 px-3 py-2 rounded-lg text-sm font-medium ${isDarkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-white hover:bg-gray-100 shadow-sm'}`}>
                📋 {gameTexts?.shopping_list_title || 'Shopping List'}
              </button>
            )}

            {!selectedMerchant ? (
              /* ===== MERCHANT LIST VIEW ===== */
              <div>
                <div className="flex items-center justify-between mb-6">
                  <button
                    onClick={async () => {
                      // Session already auto-saved — just navigate away
                      setGameStep('recipe-select');
                      setSelectedMerchant(null);
                      // Refresh active session indicator
                      if (userId && basket.length > 0) {
                        try {
                          const res = await fetch(`${API}/souk/basket/${userId}`, {
                            headers: { 'Authorization': `Bearer ${userToken}` }
                          });
                          if (res.ok) {
                            const data = await res.json();
                            if (data.is_active && data.items?.length > 0) {
                              setActiveSession(data);
                            }
                          }
                        } catch (e) { console.error(e); }
                      }
                      setSelectedRecipe(null);
                      setBasket([]);
                      setTotalDH(0);
                      setRemainingMoney(initialBudget);
                    }}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium ${isDarkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-white hover:bg-gray-100 shadow-sm'} transition-all`}
                  >
                    ← {gameTexts?.souk_back || 'Recipes'}
                  </button>
                  <h2 className="text-xl md:text-2xl font-bold">🏪 {gameTexts?.souk_title || 'The Souk'}</h2>
                  <div className="w-20" /> {/* spacer */}
                </div>
                <p className={`text-center mb-6 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {gameTexts?.souk_instructions || 'Visit merchants to find your ingredients. Tap a merchant to enter their shop.'}
                </p>

                {/* Merchants grouped by category */}
                {Object.entries(merchantsByCategory).map(([category, categoryMerchants]) => {
                  const catColor = getCategoryColor(category);
                  return (
                    <div key={category} className="mb-8">
                      <div className={`${styles.sectionHeader} ${isDarkMode ? 'border-gray-700 text-gray-300' : 'border-gray-200 text-gray-700'}`}>
                        <span>{getCategoryIcon(category)}</span>
                        <span>{categoryLabels[category] || category}</span>
                        <span className={`text-xs font-normal ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                          ({categoryMerchants.length})
                        </span>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
                        {categoryMerchants.map((merchant, idx) => {
                          const pers = getPersonality(merchant.personality);
                          return (
                            <div
                              key={merchant.id}
                              onClick={() => {
                                setGreetingMerchant(merchant);
                                setShowMerchantGreeting(true);
                              }}
                              className={`${styles.merchantCard} ${isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'} shadow-sm p-4 text-center`}
                              style={{ animation: 'none' }}
                            >
                              {/* Avatar */}
                              {merchant.avatar_url ? (
                                <img src={fixDropbox(merchant.avatar_url)} alt={merchant.name} width="80" height="80" loading="eager" decoding="async" className={`${styles.merchantAvatar} mx-auto mb-3`} />
                              ) : (
                                <div className={`${styles.merchantAvatarPlaceholder} mx-auto mb-3 ${isDarkMode ? 'bg-gray-700' : `bg-gradient-to-br ${catColor.gradient} text-white`}`}>
                                  {pers.icon}
                                </div>
                              )}
                              <h3 className="font-bold text-sm mb-1 leading-tight">{merchant.name}</h3>
                              <span className={`${styles.personalityBadge} ${isDarkMode ? pers.darkColor : pers.color} mx-auto mb-2`}>
                                {merchant.personality}
                              </span>
                              <div className={`${styles.merchantCategoryRibbon} ${isDarkMode ? catColor.dark : `${catColor.bg} ${catColor.text}`} mx-auto`}>
                                {getCategoryIcon(category)} {category}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              /* ===== INDIVIDUAL MERCHANT SHOP ===== */
              <div>
                <button
                  onClick={() => setSelectedMerchant(null)}
                  className={`mb-4 flex items-center gap-2 px-4 py-2 rounded-lg font-medium ${isDarkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-white hover:bg-gray-100 shadow-sm'} transition-all`}
                >
                  ← {gameTexts?.souk_back_to_souk || 'Back to Souk'}
                </button>

                {/* Merchant Header with speech bubble */}
                <div className={`mb-5 p-4 md:p-5 rounded-xl ${isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'} shadow-md`}>
                  <div className="flex flex-col md:flex-row md:items-start gap-3 md:gap-4">
                    {/* Mobile: avatar left + quiz right on one row. Desktop: avatar only (quiz rendered separately at end). */}
                    <div className="flex items-start justify-between md:block">
                      {selectedMerchant.avatar_url ? (
                        <img src={fixDropbox(selectedMerchant.avatar_url)} alt={selectedMerchant.name} width="80" height="80" loading="eager" decoding="async" className={styles.merchantAvatar} />
                      ) : (
                        <div className={`${styles.merchantAvatarPlaceholder} ${isDarkMode ? 'bg-gray-700' : `bg-gradient-to-br ${getCategoryColor(selectedMerchant.category).gradient} text-white`}`}>
                          {getPersonality(selectedMerchant.personality).icon}
                        </div>
                      )}
                      <button
                        onClick={() => generateMerchantQuiz(selectedMerchant)}
                        className={`md:hidden flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center text-2xl transition-all hover:scale-110 ${isDarkMode ? 'bg-blue-900 hover:bg-blue-800' : 'bg-blue-50 hover:bg-blue-100'}`}
                        title="Test your knowledge!"
                      >
                        ❓
                      </button>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <h2 className="text-xl font-bold">{selectedMerchant.name}</h2>
                        <span className={`${styles.personalityBadge} ${isDarkMode ? getPersonality(selectedMerchant.personality).darkColor : getPersonality(selectedMerchant.personality).color}`}>
                          {selectedMerchant.personality}
                        </span>
                      </div>
                      {/* Speech bubble */}
                      <div className={`${styles.speechBubble} ${isDarkMode ? 'bg-gray-700' : 'bg-blue-50'}`}>
                        <p className="text-blue-500 font-medium italic text-sm">"{selectedMerchant.greeting_darija}"</p>
                        <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{selectedMerchant.greeting_english}</p>
                      </div>
                    </div>
                    {/* Desktop-only quiz button on right */}
                    <button
                      onClick={() => generateMerchantQuiz(selectedMerchant)}
                      className={`hidden md:flex flex-shrink-0 w-12 h-12 rounded-xl items-center justify-center text-2xl transition-all hover:scale-110 ${isDarkMode ? 'bg-blue-900 hover:bg-blue-800' : 'bg-blue-50 hover:bg-blue-100'}`}
                      title="Test your knowledge!"
                    >
                      ❓
                    </button>
                  </div>
                </div>

                {/* Item Grid */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                  {merchantItems
                    .filter(item => !basket.some(b => b.id === item.id))
                    .filter((item, index, self) => index === self.findIndex((t) => t.id === item.id))
                    .map((item, idx) => {
                      const alreadyInBasket = basket.some(b => b.id === item.id);
                      const isNeeded = selectedRecipe?.required_items?.some(ri => ri.item_id === item.id);
                      return (
                        <div
                          key={item.id}
                          className={`${styles.itemCard} ${
                            alreadyInBasket
                              ? 'ring-2 ring-green-500'
                              : isNeeded
                              ? isDarkMode ? 'ring-1 ring-blue-500/50' : 'ring-1 ring-blue-300'
                              : ''
                          } ${isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'}`}
                          style={{ animation: 'none' }}
                        >
                          {/* Item image */}
                          <div className={styles.itemImageWrap}>
                            {item.image_url ? (
                              <img src={item.image_url} alt={item.name_english} width="100" height="80" loading="eager" decoding="async" />
                            ) : (
                              <div className={`${styles.itemImagePlaceholder} ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                                {getCategoryIcon(item.category || selectedMerchant.category)}
                              </div>
                            )}
                          </div>

                          <div className="p-3">
                            <h4 className="font-bold text-xs leading-tight mb-0.5">{item.name_english}</h4>
                            <p className="text-xs text-blue-500 mb-1">{item.name_darija}</p>
                            <p className={`text-xs mb-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>per {item.unit}</p>

                            <div className="flex items-center justify-between mb-2">
                              <span className={`${styles.priceTag} bg-green-100 text-green-700`}>{item.price_dh} DH</span>
                              <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>${convertToUSD(item.price_dh)}</span>
                            </div>

                            {isNeeded && (
                              <div className="mb-2">
                                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">{gameTexts?.item_needed || 'Needed'}</span>
                              </div>
                            )}

                            {alreadyInBasket ? (
                              <div className="px-2 py-1.5 bg-green-600 text-white rounded-lg text-center text-xs font-bold">
                                ✓ {gameTexts?.item_in_basket || 'In Basket'}
                              </div>
                            ) : (
                              <div className="space-y-1.5">
                                <div className="flex gap-1.5">
                                  <button
                                    onClick={() => addItemToBasket(item, selectedMerchant)}
                                    className="flex-1 px-2 py-1.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg text-xs font-medium hover:shadow-md transition-all"
                                  >
                                    🛒 {gameTexts?.item_add || 'Add'}
                                  </button>
                                  <button
                                    onClick={() => setBargainingItem({ item, merchant: selectedMerchant })}
                                    className="px-3 py-1.5 bg-gradient-to-r from-orange-500 to-amber-600 text-white rounded-lg text-xs font-medium hover:shadow-md transition-all"
                                    title="Bargain"
                                  >
                                    💰
                                  </button>
                                </div>
                                <button
                                  onClick={() => getPriceComparison(item)}
                                  className={`w-full text-xs py-1 rounded-lg font-medium ${isDarkMode ? 'text-purple-400 hover:bg-purple-900/30' : 'text-purple-600 hover:bg-purple-50'} transition-all`}
                                >
                                  {gameTexts?.item_compare || 'Compare Prices'}
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ===== STEP 4: CHECKOUT ===== */}
      {gameStep === 'checkout' && (
        <div className="max-w-3xl mx-auto">
          <button
            onClick={() => setGameStep('merchant-shop')}
            className={`mb-4 flex items-center gap-2 px-4 py-2 rounded-lg font-medium ${isDarkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-white hover:bg-gray-100 shadow-sm'} transition-all`}
          >
            ← {gameTexts?.checkout_continue || 'Continue Shopping'}
          </button>

          <div className={`p-5 md:p-6 rounded-xl ${isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'} shadow-md mb-6`}>
            <h2 className="text-2xl font-bold mb-5">🛒 {gameTexts?.checkout_title || 'Your Basket'}</h2>

            {basket.length > 0 ? (
              <div className="space-y-5 mb-6">
                {/* Items Needed for Recipe */}
                <div>
                  <h3 className="text-sm font-bold mb-3 text-green-600 flex items-center gap-2">
                    ✅ {gameTexts?.checkout_for_recipe || 'For'} {selectedRecipe.name_english}
                  </h3>
                  <div className="space-y-2">
                    {basket.filter(item => selectedRecipe.required_items.map(ri => ri.item_id).includes(item.id)).map((item, index) => (
                      <div key={index} className={`flex items-center gap-3 p-3 rounded-lg ${isDarkMode ? 'bg-gray-700/50' : 'bg-green-50'}`}>
                        {/* Thumbnail */}
                        <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 flex items-center justify-center bg-green-100 text-lg" style={{width:40,height:40}}>
                          {item.image_url ? <img src={item.image_url} alt="" width="40" height="40" loading="eager" decoding="async" className="w-full h-full object-cover" /> : getCategoryIcon(item.category)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-bold text-sm">{item.name_english}</p>
                          <p className="text-xs text-blue-500">{item.name_darija}</p>
                          <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>From: {item.merchant_name}</p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="font-bold text-green-600 text-sm">{item.price_paid.toFixed(2)} DH</p>
                          <button
                            onClick={() => {
                              const newBasket = basket.filter((_, i) => basket.indexOf(item) !== i);
                              setBasket(newBasket);
                              const total = newBasket.reduce((sum, item) => sum + item.price_paid, 0);
                              setTotalDH(total);
                              setRemainingMoney(initialBudget - total);
                              saveSession(newBasket, total, initialBudget - total);
                            }}
                            className="text-xs text-red-500 hover:underline"
                          >
                            {gameTexts?.checkout_remove || 'Remove'}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Items NOT Needed */}
                {basket.filter(item => !selectedRecipe.required_items.map(ri => ri.item_id).includes(item.id)).length > 0 && (
                  <div>
                    <h3 className="text-sm font-bold mb-3 text-orange-600 flex items-center gap-2">
                      ⚠️ {gameTexts?.checkout_extra_items || 'Extra items (not needed for recipe)'}
                    </h3>
                    <div className="space-y-2">
                      {basket.filter(item => !selectedRecipe.required_items.map(ri => ri.item_id).includes(item.id)).map((item, index) => (
                        <div key={index} className={`flex items-center gap-3 p-3 rounded-lg border border-orange-300 ${isDarkMode ? 'bg-orange-900/20' : 'bg-orange-50'}`}>
                          <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 flex items-center justify-center bg-orange-100 text-lg" style={{width:40,height:40}}>
                            {item.image_url ? <img src={item.image_url} alt="" width="40" height="40" loading="eager" decoding="async" className="w-full h-full object-cover" /> : getCategoryIcon(item.category)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-bold text-sm">{item.name_english}</p>
                            <p className="text-xs text-blue-500">{item.name_darija}</p>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <p className="font-bold text-orange-600 text-sm">{item.price_paid.toFixed(2)} DH</p>
                            <button
                              onClick={() => {
                                const newBasket = basket.filter((_, i) => basket.indexOf(item) !== i);
                                setBasket(newBasket);
                                const total = newBasket.reduce((sum, item) => sum + item.price_paid, 0);
                                setTotalDH(total);
                                setRemainingMoney(initialBudget - total);
                                saveSession(newBasket, total, initialBudget - total);
                              }}
                              className="text-xs text-red-500 hover:underline"
                            >
                              {gameTexts?.checkout_remove || 'Remove'}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <p className={`text-center py-8 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>{gameTexts?.checkout_empty || 'Empty basket. Start shopping!'}</p>
            )}

            {/* Total & Actions */}
            <div className={`border-t pt-4 mt-4 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex items-center justify-between mb-5">
                <span className="text-lg font-bold">{gameTexts?.checkout_total || 'Total'}</span>
                <div className="text-right">
                  <p className="text-2xl font-black text-green-600">{totalDH.toFixed(2)} DH</p>
                  <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>≈ ${convertToUSD(totalDH)} USD</p>
                </div>
              </div>

              {basket.length > 0 && (
                <button
                  onClick={analyzeShoppingChoices}
                  className={`w-full mb-3 px-5 py-3 rounded-xl font-bold text-sm transition-all ${isDarkMode ? 'bg-purple-900/50 text-purple-300 hover:bg-purple-900' : 'bg-purple-50 text-purple-700 hover:bg-purple-100'} border ${isDarkMode ? 'border-purple-700' : 'border-purple-200'}`}
                >
                  🧾 {gameTexts?.checkout_analyze || 'Analyze My Shopping'}
                </button>
              )}

              {getMissingItems().length > 0 ? (
                <div className={`p-4 rounded-xl mb-4 ${isDarkMode ? 'bg-red-900/30 border border-red-800' : 'bg-red-50 border border-red-200'}`}>
                  <p className="font-bold mb-2 text-red-600 text-sm">{(gameTexts?.checkout_missing || 'Missing {count} ingredient(s):').replace('{count}', getMissingItems().length)}</p>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {getMissingItems().map(item => (
                      <span key={item.id} className={`text-xs px-2 py-1 rounded-full ${isDarkMode ? 'bg-red-900 text-red-300' : 'bg-red-100 text-red-700'}`}>
                        {item.name_english}
                      </span>
                    ))}
                  </div>
                  <button
                    onClick={() => setGameStep('merchant-shop')}
                    className="w-full px-4 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 font-bold text-sm transition-all"
                  >
                    ← {gameTexts?.souk_back_to_souk || 'Back to Souk'}
                  </button>
                </div>
              ) : basket.length > 0 && (
                <div className={`p-4 rounded-xl mb-4 ${isDarkMode ? 'bg-green-900/30 border border-green-800' : 'bg-green-50 border border-green-200'}`}>
                  <p className="font-bold mb-3 text-green-600 flex items-center gap-2">
                    ✅ {gameTexts?.checkout_all_collected || 'All ingredients collected!'}
                  </p>
                  <button
                    onClick={startCooking}
                    className="w-full px-6 py-4 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl font-bold text-lg hover:shadow-lg transition-all"
                  >
                    🍳 {gameTexts?.checkout_start_cooking || 'Start Cooking!'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ===== RECIPE CONFIRMATION POPUP ===== */}
      {showRecipeConfirm && selectedRecipe && (
        <div className={styles.modalOverlay}>
          <div className={`${styles.modalContent} max-w-md w-full rounded-2xl overflow-hidden ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-2xl`}>
            {/* Recipe image header */}
            <div className="h-40 relative overflow-hidden" style={{height:160}}>
              {selectedRecipe.image_url ? (
                <img src={selectedRecipe.image_url} alt={selectedRecipe.name_english} width="800" height="160" loading="eager" decoding="async" className="w-full h-full object-cover" />
              ) : (
                <div className={`w-full h-full flex items-center justify-center text-6xl ${isDarkMode ? 'bg-gradient-to-br from-orange-900 to-red-900' : 'bg-gradient-to-br from-orange-200 to-red-200'}`}>
                  🍲
                </div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              <div className="absolute bottom-3 left-4 text-white">
                <h3 className="text-xl font-bold">{selectedRecipe.name_english}</h3>
                <p className="text-sm text-white/80">{selectedRecipe.name_darija}</p>
              </div>
            </div>

            <div className="p-5">
              {/* Budget */}
              <div className={`p-3 rounded-xl mb-4 ${isDarkMode ? 'bg-yellow-900/30 border border-yellow-800' : 'bg-yellow-50 border border-yellow-200'}`}>
                <div className="flex items-center gap-3">
                  <span className="text-2xl">💰</span>
                  <div className="flex-1">
                    <p className={`text-xs ${isDarkMode ? 'text-yellow-400' : 'text-yellow-700'}`}>{gameTexts?.recipe_confirm_budget || 'Shopping Budget'}</p>
                    <p className="text-xl font-black text-green-600">{Math.ceil(selectedRecipe.estimated_cost_dh * 1.2).toFixed(2)} DH</p>
                  </div>
                </div>
                <p className={`text-xs mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {gameTexts?.recipe_confirm_tip || 'Tip: Bargain with merchants to save money!'}
                </p>
              </div>

              <p className={`mb-5 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {(gameTexts?.recipe_confirm_instructions || 'Find all {item_count} ingredients across {merchant_count} merchants. Good luck!').replace('{item_count}', selectedRecipe.required_items.length).replace('{merchant_count}', merchants.length)}
              </p>

              <div className="flex gap-3">
                <button
                  onClick={() => { setShowRecipeConfirm(false); setGameStep('recipe-select'); }}
                  className={`flex-1 px-5 py-3 rounded-xl font-bold text-sm ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'} transition-all`}
                >
                  ← Back
                </button>
                <button
                  onClick={confirmAndStartShopping}
                  className="flex-1 px-5 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl font-bold text-sm hover:shadow-lg transition-all"
                >
                  🛒 {gameTexts?.recipe_confirm_start || 'Start Shopping!'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Merchant Greeting Modal */}
      {/* ===== MERCHANT GREETING MODAL ===== */}
      {showMerchantGreeting && greetingMerchant && (
        <div className={styles.modalOverlay}>
          <div className={`${styles.modalContent} max-w-md w-full rounded-2xl overflow-hidden ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-2xl`}>
            {/* Merchant header */}
            <div className={`p-6 text-center bg-gradient-to-br ${getCategoryColor(greetingMerchant.category).gradient} text-white`}>
              {greetingMerchant.avatar_url ? (
                <img src={fixDropbox(greetingMerchant.avatar_url)} alt={greetingMerchant.name} loading="eager" decoding="async" className={`${styles.merchantAvatar} mx-auto mb-3 border-white/40`} style={{ width: 100, height: 100 }} />
              ) : (
                <div className="text-6xl mb-3">{getPersonality(greetingMerchant.personality).icon}</div>
              )}
              <h2 className="text-2xl font-bold">{greetingMerchant.name}</h2>
              <span className="inline-block mt-1 px-3 py-0.5 bg-white/20 rounded-full text-xs capitalize font-medium backdrop-blur-sm">
                {greetingMerchant.personality}
              </span>
            </div>

            <div className="p-6">
              {/* Speech bubble */}
              <div className={`${styles.speechBubble} ${isDarkMode ? 'bg-blue-900/40' : 'bg-blue-50'} mb-4`}>
                <p className="text-blue-500 font-medium italic">"{greetingMerchant.greeting_darija}"</p>
                <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{greetingMerchant.greeting_english}</p>
              </div>

              {(greetingMerchant.greeting_full || greetingMerchant.description) && (
                <p className={`text-sm mb-5 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  {greetingMerchant.greeting_full || greetingMerchant.description}
                </p>
              )}

              <div className="flex gap-3">
                <button
                  onClick={() => { setShowMerchantGreeting(false); setGreetingMerchant(null); }}
                  className={`flex-1 px-5 py-3 rounded-xl font-bold text-sm ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'} transition-all`}
                >
                  ← Back
                </button>
                <button
                  onClick={() => { setSelectedMerchant(greetingMerchant); setShowMerchantGreeting(false); setGreetingMerchant(null); }}
                  className="flex-1 px-5 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl font-bold text-sm hover:shadow-lg transition-all"
                >
                  🛒 {gameTexts?.souk_enter_shop || 'Enter Shop'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Duplicate Item Warning */}
      {duplicateWarning && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`max-w-md w-full p-6 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-2xl`}>
            <div className="text-7xl text-center mb-4">🛑</div>
            <h3 className="text-2xl font-bold text-center mb-4">{gameTexts?.warning_duplicate_title || 'Already in Basket!'}</h3>
            <div className="text-center mb-6">
              <p className="text-lg font-bold mb-2">{duplicateWarning.item}</p>
              <p className="text-blue-500 italic mb-3">({duplicateWarning.itemDarija})</p>
              <p>{duplicateWarning.message}</p>
            </div>
            <button
              onClick={() => setDuplicateWarning(null)}
              className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-bold"
            >
              Got it! ✓
            </button>
          </div>
        </div>
      )}

      {/* Wrong Item Warning */}
      {wrongItemWarning && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`max-w-md w-full p-6 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-2xl`}>
            <div className="text-7xl text-center mb-4">⚠️</div>
            <h3 className="text-2xl font-bold text-center mb-4">{gameTexts?.warning_wrong_item_title || 'Wrong Item!'}</h3>
            <div className="text-center mb-6">
              <p className="text-lg font-bold mb-2">{wrongItemWarning.item?.name_english}</p>
              <p className="text-blue-500 italic mb-3">({wrongItemWarning.item?.name_darija})</p>
              <p>{wrongItemWarning.message}</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setWrongItemWarning(null)}
                className={`flex-1 px-6 py-3 rounded-lg font-bold ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-300 hover:bg-gray-400'}`}
              >
                ← Cancel
              </button>
              <button
                onClick={() => {
                  // Add the item anyway (user wants to buy wrong item)
                  const { item, merchant, bargainedPrice, quantity } = wrongItemWarning;
                  const finalPrice = bargainedPrice || item.price_dh;
                  const newBasket = [...basket, { 
                    ...item, 
                    merchant_name: merchant.name, 
                    merchant_id: merchant.id, 
                    price_paid: finalPrice,
                    quantity: quantity || '1 unit'
                  }];
                  setBasket(newBasket);
                  const total = newBasket.reduce((sum, item) => sum + item.price_paid, 0);
                  setTotalDH(total);
                  setRemainingMoney(initialBudget - total);
                  saveSession(newBasket, total, initialBudget - total);
                  setWrongItemWarning(null);
                }}
                className="flex-1 px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 font-bold"
              >
                Buy Anyway →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Budget Warning Modal */}
      {showBudgetWarning && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`max-w-md w-full p-6 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-2xl`}>
            <div className="text-7xl text-center mb-4 animate-pulse">💸</div>
            <h3 className="text-2xl font-bold text-center mb-4 text-red-600">{gameTexts?.warning_no_money_title || 'Not Enough Money!'}</h3>
            <div className={`p-4 rounded-lg mb-4 ${isDarkMode ? 'bg-gray-700' : 'bg-red-50'}`}>
              <p className="text-lg font-bold mb-2">{showBudgetWarning.item?.name_english}</p>
              <p className="text-blue-500 italic mb-3">({showBudgetWarning.item?.name_darija})</p>
              <p className="mb-3">{showBudgetWarning.message}</p>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className={`p-2 rounded ${isDarkMode ? 'bg-gray-600' : 'bg-white'}`}>
                  <p className="opacity-70">Item Cost:</p>
                  <p className="font-bold text-orange-600">{showBudgetWarning.price?.toFixed(2)} DH</p>
                </div>
                <div className={`p-2 rounded ${isDarkMode ? 'bg-gray-600' : 'bg-white'}`}>
                  <p className="opacity-70">You Have:</p>
                  <p className="font-bold text-green-600">{(initialBudget - totalDH).toFixed(2)} DH</p>
                </div>
              </div>
            </div>
            <div className={`p-3 rounded-lg mb-4 ${isDarkMode ? 'bg-blue-900' : 'bg-blue-50'}`}>
              <p className="text-sm font-bold text-blue-600 mb-1">💡 {gameTexts?.warning_no_money_tip || 'What you can do:'}</p>
              <ul className="text-sm space-y-1">
                <li>• {gameTexts?.warning_no_money_bargain || 'Try bargaining with the merchant'}</li>
                <li>• {gameTexts?.warning_no_money_remove || 'Remove some items from your basket'}</li>
                <li>• {gameTexts?.warning_no_money_cheaper || 'Look for cheaper alternatives'}</li>
              </ul>
            </div>
            <button
              onClick={() => setShowBudgetWarning(false)}
              className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-bold"
            >
              Got it! I'll bargain more 💪
            </button>
          </div>
        </div>
      )}

      {/* Merchant Dialogue Popup */}
      {merchantDialogue && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`max-w-md w-full p-6 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-2xl`}>
            <div className="text-center">
              {merchantDialogue.avatar_url ? (
                <img src={fixDropbox(merchantDialogue.avatar_url)} alt="" loading="eager" decoding="async" className="w-24 h-24 rounded-full object-cover mx-auto mb-4 border-4 border-white shadow-lg" />
              ) : (
                <div className="text-7xl mb-4">{merchantDialogue.icon}</div>
              )}
              <h3 className="text-2xl font-bold mb-3">{merchantDialogue.merchant} says:</h3>
              <p className="text-lg mb-6">{merchantDialogue.message}</p>
              <button
                onClick={() => {
                  setMerchantDialogue(null);
                  setSelectedMerchant(null);
                }}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-bold"
              >
                Okay!
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== BARGAINING MODAL ===== */}
      {bargainingItem && (
        <div className={styles.modalOverlay}>
          <div className={`${styles.modalContent} max-w-md w-full p-6 rounded-2xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-2xl`}>
            {/* Header */}
            <div className="flex items-center gap-3 mb-4">
              {bargainingItem.merchant.avatar_url ? (
                <img src={fixDropbox(bargainingItem.merchant.avatar_url)} alt="" loading="eager" decoding="async" className="w-12 h-12 rounded-full object-cover" />
              ) : (
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-orange-500 to-amber-600 text-white flex items-center justify-center text-xl">
                  {getPersonality(bargainingItem.merchant.personality).icon}
                </div>
              )}
              <div>
                <h3 className="font-bold">{(gameTexts?.bargain_title || 'Bargaining with {merchant}').replace('{merchant}', bargainingItem.merchant.name)}</h3>
                <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{bargainingItem.item.name_english} ({bargainingItem.item.name_darija})</p>
              </div>
            </div>

            {/* Price display */}
            <div className={`p-4 rounded-xl mb-4 text-center ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
              <p className={`text-xs mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{gameTexts?.bargain_asking_price || 'Asking Price'}</p>
              <p className={`text-3xl font-black ${bargainResult && bargainResult.accepted ? 'line-through text-gray-400' : 'text-red-500'}`}>
                {bargainingItem.item.price_dh} DH
              </p>
              <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>≈ ${convertToUSD(bargainingItem.item.price_dh)} USD</p>
            </div>

            {!bargainResult ? (
              <div>
                <div className={`p-3 rounded-lg text-xs mb-4 ${isDarkMode ? 'bg-amber-900/30 text-amber-300' : 'bg-amber-50 text-amber-700'}`}>
                  💡 {gameTexts?.bargain_tip || 'Make an offer below the asking price. The lower, the more you save!'}
                </div>
                <div className="relative mb-4">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 font-bold text-lg text-gray-400">DH</span>
                  <input
                    type="number"
                    step="0.1"
                    value={userOffer}
                    onChange={(e) => setUserOffer(e.target.value)}
                    className={`w-full pl-14 pr-4 py-3.5 rounded-xl border-2 text-xl font-bold ${
                      isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                    } focus:border-orange-500 focus:ring-2 focus:ring-orange-200 outline-none transition-all`}
                    placeholder={gameTexts?.bargain_placeholder || 'Your offer...'}
                  />
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => { setBargainingItem(null); setUserOffer(''); }}
                    className={`flex-1 px-4 py-3 rounded-xl font-bold text-sm ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'} transition-all`}
                  >
                    {gameTexts?.bargain_cancel || 'Cancel'}
                  </button>
                  <button
                    onClick={submitBargain}
                    disabled={!userOffer || parseFloat(userOffer) <= 0}
                    className="flex-1 px-4 py-3 bg-gradient-to-r from-orange-500 to-amber-600 text-white rounded-xl font-bold text-sm hover:shadow-lg disabled:opacity-50 transition-all"
                  >
                    💰 {gameTexts?.bargain_make_offer || 'Make Offer'}
                  </button>
                </div>
              </div>
            ) : (
              <div className={bargainResult.accepted ? styles.bargainSuccess : styles.bargainFail}>
                <div className="text-center">
                  <div className="text-5xl mb-3">{bargainResult.icon}</div>

                  {bargainResult.accepted ? (
                    <>
                      <p className="font-bold text-lg mb-2">{bargainResult.message}</p>
                      <div className={`inline-block px-5 py-2 rounded-full mb-3 ${isDarkMode ? 'bg-green-900 text-green-300' : 'bg-green-100 text-green-700'}`}>
                        <span className="text-2xl font-black">{bargainResult.finalPrice.toFixed(2)} DH</span>
                      </div>
                      <p className="text-green-600 font-bold">
                        Saved {bargainResult.savedAmount.toFixed(2)} DH ({bargainResult.savedPercent}%)
                      </p>
                      <p className={`text-xs mt-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Adding to basket...</p>
                    </>
                  ) : (
                    <>
                      <p className="font-bold mb-2">{bargainResult.message}</p>
                      <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        Minimum: {bargainResult.minPrice.toFixed(2)} DH
                      </p>
                      <div className="flex gap-3">
                        <button
                          onClick={() => { setBargainResult(null); setUserOffer(bargainResult.minPrice.toFixed(2)); }}
                          className="flex-1 px-4 py-2.5 bg-gradient-to-r from-orange-500 to-amber-600 text-white rounded-xl font-bold text-sm"
                        >
                          {gameTexts?.bargain_try_again || 'Try Again'}
                        </button>
                        <button
                          onClick={() => { addItemToBasket(bargainingItem.item, bargainingItem.merchant); setBargainingItem(null); setBargainResult(null); }}
                          className={`flex-1 px-4 py-2.5 rounded-xl font-bold text-sm ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'}`}
                        >
                          {gameTexts?.bargain_full_price || 'Full Price'}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Merchant Quiz Modal */}
      {showMerchantQuiz && quizQuestion && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className={`max-w-lg w-full p-8 rounded-xl relative ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-2xl`}>
            {/* Close button */}
            <button
              onClick={() => { setShowMerchantQuiz(false); setQuizQuestion(null); setQuizAnswer(null); setShowQuizResult(false); }}
              className={`absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center text-lg font-bold transition-colors ${isDarkMode ? 'text-gray-400 hover:text-white hover:bg-gray-700' : 'text-gray-400 hover:text-gray-800 hover:bg-gray-100'}`}
            >&times;</button>
            <div className="text-center">
              <div className="text-6xl mb-4">
                {quizQuestion.merchant.personality === 'helpful' ? '😊' : quizQuestion.merchant.personality === 'generous' ? '🤗' : '😄'}
              </div>
              <h2 className="text-2xl font-bold mb-3">{(gameTexts?.quiz_merchant_asks || '{merchant} asks:').replace('{merchant}', quizQuestion.merchant.name)}</h2>

              <div className={`p-4 rounded-lg mb-6 ${isDarkMode ? 'bg-gray-700' : 'bg-blue-50'}`}>
                <p className="text-lg font-bold mb-2">{quizQuestion.item.name_english}</p>
                <p className="text-blue-500 italic mb-3">({quizQuestion.item.name_darija})</p>
                <p className="text-xl font-bold">{(gameTexts?.quiz_how_much || 'How much do you need for {recipe}?').replace('{recipe}', selectedRecipe.name_english)}</p>
              </div>

              {!showQuizResult ? (
                <div className="space-y-3">
                  {quizQuestion.options.map((option, index) => (
                    <button
                      key={index}
                      onClick={() => handleQuizAnswer(option)}
                      className="w-full px-6 py-4 bg-gradient-to-r from-green-600 to-blue-600 text-white rounded-xl font-bold text-xl hover:shadow-lg transition-all hover:scale-105"
                    >
                      {option} {quizQuestion.unit}
                    </button>
                  ))}
                </div>
              ) : (
                <div>
                  {quizAnswer === quizQuestion.correctQuantity ? (
                    <div className="bg-green-100 text-green-800 p-6 rounded-lg">
                      <div className="text-5xl mb-3">✅</div>
                      <p className="text-xl font-bold mb-2">{gameTexts?.quiz_correct || 'Correct! Mezyan!'}</p>
                      <p>{(gameTexts?.quiz_correct_detail || 'You need {quantity} {unit} of {item}!').replace('{quantity}', quizQuestion.correctQuantity).replace('{unit}', quizQuestion.unit).replace('{item}', quizQuestion.item.name_darija)}</p>
                    </div>
                  ) : (
                    <div className="bg-orange-100 text-orange-800 p-6 rounded-lg">
                      <div className="text-5xl mb-3">💡</div>
                      <p className="text-xl font-bold mb-2">{gameTexts?.quiz_wrong || 'Not quite!'}</p>
                      <p className="mb-2">{(gameTexts?.quiz_you_selected || 'You selected: {quantity} {unit}').replace('{quantity}', quizAnswer).replace('{unit}', quizQuestion.unit)}</p>
                      <p className="font-bold">{(gameTexts?.quiz_hint || 'Hint: You need only {quantity} {unit} of {item} for {recipe}!').replace('{quantity}', quizQuestion.correctQuantity).replace('{unit}', quizQuestion.unit).replace('{item}', quizQuestion.item.name_darija).replace('{recipe}', selectedRecipe.name_darija)}</p>
                      <p className="text-sm mt-3">{gameTexts?.quiz_remember || 'Remember this for next time!'} 📝</p>
                    </div>
                  )}
                  <button
                    onClick={() => { setShowMerchantQuiz(false); setQuizQuestion(null); setQuizAnswer(null); setShowQuizResult(false); }}
                    className="mt-4 w-full px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-bold hover:shadow-lg transition-all"
                  >
                    {gameTexts?.quiz_continue || 'Continue Shopping'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Price Comparison Modal */}
      {showPriceComparison && comparisonItem && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className={`max-w-lg w-full p-8 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-2xl`}>
            <div className="text-center">
              <div className="text-5xl mb-4">💰</div>
              <h2 className="text-2xl font-bold mb-3">{gameTexts?.compare_title || 'Price Comparison'}</h2>
              
              <div className={`p-4 rounded-lg mb-6 ${isDarkMode ? 'bg-gray-700' : 'bg-purple-50'}`}>
                <p className="text-lg font-bold mb-1">{comparisonItem.item.name_english}</p>
                <p className="text-blue-500 italic">{comparisonItem.item.name_darija}</p>
              </div>

              <div className="space-y-3 mb-6">
                {comparisonItem.prices.sort((a, b) => a.price - b.price).map((priceInfo, index) => {
                  const isCheapest = index === 0;
                  const isMostExpensive = index === comparisonItem.prices.length - 1;
                  
                  return (
                    <div
                      key={index}
                      className={`p-4 rounded-lg border-2 ${
                        isCheapest
                          ? 'border-green-500 bg-green-50'
                          : isMostExpensive
                          ? 'border-red-500 bg-red-50'
                          : isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-100 border-gray-300'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-bold">{priceInfo.merchant}</p>
                          <p className="text-xs capitalize opacity-70">{priceInfo.personality}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold">{priceInfo.price} DH</p>
                          {isCheapest && <p className="text-xs text-green-600 font-bold">{gameTexts?.compare_best_deal || 'Best Deal!'} 🎉</p>}
                          {isMostExpensive && <p className="text-xs text-red-600 font-bold">{gameTexts?.compare_most_expensive || 'Most Expensive'}</p>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <button
                onClick={() => {
                  setShowPriceComparison(false);
                  setComparisonItem(null);
                }}
                className="w-full px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg font-bold hover:shadow-lg"
              >
                Got it! 👍
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Shopping Analysis Modal */}
      {showShoppingAnalysis && shoppingAnalysis && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className={`max-w-3xl w-full p-8 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-2xl max-h-[90vh] overflow-y-auto`}>
            <div className="text-center mb-6">
              <div className="text-6xl mb-4">🧾</div>
              <h2 className="text-3xl font-bold mb-2">{gameTexts?.analysis_title || 'Shopping Analysis'}</h2>
              <p className="opacity-70">{gameTexts?.analysis_subtitle || "Let's see how smart you shopped!"}</p>
            </div>

            {/* Total Summary */}
            <div className={`p-4 rounded-xl mb-6 ${isDarkMode ? 'bg-gray-700' : 'bg-blue-50'}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="font-bold">{gameTexts?.analysis_total_spent || 'Total Spent:'}</span>
                <span className="text-xl font-bold">{shoppingAnalysis.totalSpent.toFixed(2)} DH</span>
              </div>
              {shoppingAnalysis.totalSavings > 0 && (
                <div className="flex items-center justify-between">
                  <span className="font-bold">{gameTexts?.analysis_could_save || 'Could Have Saved:'}</span>
                  <span className="text-xl font-bold text-orange-600">{shoppingAnalysis.totalSavings.toFixed(2)} DH</span>
                </div>
              )}
            </div>

            {/* Items Analysis */}
            <div className="space-y-4 mb-6">
              {shoppingAnalysis.items.map((analysis, index) => (
                <div 
                  key={index} 
                  className={`p-4 rounded-xl border-2 ${
                    analysis.color === 'green' 
                      ? 'border-green-500 bg-green-50' 
                      : analysis.color === 'orange' 
                      ? 'border-orange-500 bg-orange-50' 
                      : isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-100 border-gray-300'
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <p className="font-bold">{analysis.item.name_english}</p>
                      <p className="text-sm text-blue-500">{analysis.item.name_darija}</p>
                      <p className="text-xs opacity-70 mt-1">From: {analysis.item.merchant_name}</p>
                      {!analysis.isNeeded && (
                        <p className="text-xs text-orange-600 font-bold mt-1">⚠️ {gameTexts?.analysis_not_needed || 'Not needed for recipe'}</p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="font-bold">{analysis.yourPrice.toFixed(2)} DH</p>
                      <p className="text-xs opacity-70">${convertToUSD(analysis.yourPrice)}</p>
                    </div>
                  </div>

                  <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
                    <p className="text-sm mb-2">{analysis.verdict}</p>
                    
                    {/* Price comparison across merchants */}
                    <div className="text-xs space-y-1">
                      {analysis.allPrices.map((priceInfo, i) => (
                        <div key={i} className="flex items-center justify-between">
                          <span className={priceInfo.merchantId === analysis.item.merchant_id ? 'font-bold' : ''}>
                            {priceInfo.merchant}:
                          </span>
                          <span className={priceInfo.price === analysis.cheapestPrice ? 'text-green-600 font-bold' : ''}>
                            {priceInfo.price.toFixed(2)} DH
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <button
              onClick={() => setShowShoppingAnalysis(false)}
              className="w-full px-6 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-bold text-lg hover:shadow-lg transition-all"
            >
              Got it! Learn & Improve 📚
            </button>
          </div>
        </div>
      )}

      {/* Cooking Instructions Modal */}
      {showCookingInstructions && selectedRecipe && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className={`max-w-4xl w-full p-8 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-2xl max-h-[90vh] overflow-y-auto`}>
            <div className="mb-6">
              <div className="text-6xl mb-4 text-center">👨‍🍳</div>
              <h2 className="text-3xl font-bold text-center mb-2">{selectedRecipe.name_english}</h2>
              <p className="text-2xl text-blue-500 italic text-center mb-4">{selectedRecipe.name_darija}</p>
              <p className="text-center opacity-70 mb-6">{selectedRecipe.description}</p>
              
              {/* Time & Serves Info */}
              <div className={`grid grid-cols-3 gap-4 p-4 rounded-lg mb-6 ${isDarkMode ? 'bg-gray-700' : 'bg-blue-50'}`}>
                <div className="text-center">
                  <p className="text-sm opacity-70">{gameTexts?.cooking_prep_time || 'Prep Time'}</p>
                  <p className="text-xl font-bold">{selectedRecipe.prep_time_minutes || 15} min</p>
                </div>
                <div className="text-center">
                  <p className="text-sm opacity-70">{gameTexts?.cooking_cook_time || 'Cook Time'}</p>
                  <p className="text-xl font-bold">{selectedRecipe.cooking_time_minutes} min</p>
                </div>
                <div className="text-center">
                  <p className="text-sm opacity-70">{gameTexts?.cooking_servings || 'Servings'}</p>
                  <p className="text-xl font-bold">{selectedRecipe.serves}</p>
                </div>
              </div>
            </div>

            {/* Cooking Instructions */}
            <div className="mb-6">
              <h3 className="text-2xl font-bold mb-4 flex items-center gap-2">
                <span>📋</span> {gameTexts?.cooking_instructions_title || 'Cooking Instructions'}
              </h3>
              <div className="space-y-4">
                {selectedRecipe.instructions && selectedRecipe.instructions.map((instruction, index) => (
                  <div key={index} className={`flex gap-4 p-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold">
                      {index + 1}
                    </div>
                    <p className="flex-1 pt-1">{instruction}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Tips */}
            {selectedRecipe.tips && selectedRecipe.tips.length > 0 && (
              <div className="mb-6">
                <h3 className="text-xl font-bold mb-3 flex items-center gap-2">
                  <span>💡</span> {gameTexts?.cooking_pro_tips || 'Pro Tips'}
                </h3>
                <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-yellow-900' : 'bg-yellow-50'}`}>
                  <ul className="list-disc list-inside space-y-2">
                    {selectedRecipe.tips.map((tip, index) => (
                      <li key={index} className="text-sm">{tip}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => setShowCookingInstructions(false)}
                className={`flex-1 px-6 py-3 rounded-xl font-bold ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'}`}
              >
                Close
              </button>
              <button
                onClick={resetGame}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-bold hover:shadow-lg"
              >
                🍽️ {gameTexts?.success_cook_another || 'Cook Another Recipe'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal - Cooking Complete */}
      {showSuccessModal && cookingResult && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className={`max-w-2xl w-full p-8 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-2xl`}>
            <div className="text-center">
              <div className="text-8xl mb-4 animate-bounce">🎉</div>
              <h2 className="text-4xl font-bold mb-3 text-green-600">{gameTexts?.success_title || 'Cooking Complete!'}</h2>
              <p className="text-2xl mb-2">{cookingResult.recipeName}</p>
              <p className="text-xl text-blue-500 mb-6 italic">{cookingResult.recipeDarija}</p>
              
              <div className={`p-6 rounded-xl mb-6 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                <div className="grid grid-cols-2 gap-4 text-left">
                  <div>
                    <p className="text-sm opacity-70 mb-1">{gameTexts?.success_total_spent || 'Total Spent'}</p>
                    <p className="text-2xl font-bold text-red-600">{cookingResult.totalSpent.toFixed(2)} DH</p>
                  </div>
                  <div>
                    <p className="text-sm opacity-70 mb-1">{gameTexts?.success_money_saved || 'Money Saved'}</p>
                    <p className="text-2xl font-bold text-green-600">{cookingResult.savings.toFixed(2)} DH</p>
                  </div>
                  <div>
                    <p className="text-sm opacity-70 mb-1">{gameTexts?.success_items_bought || 'Items Bought'}</p>
                    <p className="text-2xl font-bold">{cookingResult.itemsCount} items</p>
                  </div>
                </div>
              </div>

              {cookingResult.savings > 0 && (
                <div className="bg-green-100 text-green-800 p-4 rounded-lg mb-6">
                  <p className="font-bold">🎊 {gameTexts?.success_great_bargain || 'Great Bargaining!'}</p>
                  <p className="text-sm">{gameTexts?.success_great_bargain_desc || 'You saved money by negotiating with merchants!'}</p>
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowSuccessModal(false);
                    setShowCookingInstructions(true);
                  }}
                  className="flex-1 px-6 py-4 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-xl font-bold text-lg hover:shadow-lg transition-all hover:scale-105"
                >
                  👨‍🍳 {gameTexts?.success_how_to_cook || 'How to Cook'}
                </button>
                <button
                  onClick={resetGame}
                  className="flex-1 px-6 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-bold text-lg hover:shadow-lg transition-all hover:scale-105"
                >
                  🍽️ {gameTexts?.success_cook_another || 'Cook Another Recipe'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Help Modal */}
      {showHelpModal && gameTexts && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`max-w-2xl w-full p-8 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-2xl max-h-[80vh] overflow-y-auto`}>
            <div className="flex items-center justify-between mb-6">
              <h3 className={`text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                🎮 {gameTexts?.help_title || 'How to Play'}
              </h3>
              <button
                onClick={() => setShowHelpModal(false)}
                className={`text-3xl ${isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-800'}`}
              >
                ×
              </button>
            </div>
            
            <div className={`prose ${isDarkMode ? 'prose-invert' : ''} max-w-none`}>
              <div className={`whitespace-pre-wrap ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                {gameTexts.help_text}
              </div>
            </div>

            <button
              onClick={() => setShowHelpModal(false)}
              className="mt-6 w-full px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:shadow-lg font-bold"
            >
              {gameTexts?.help_button || "Got it! Let's Play"} 🎮
            </button>
          </div>
        </div>
      )}

      {/* ===== MY PROGRESS MODAL ===== */}
      {showProgressModal && (
        <div className={styles.modalOverlay}>
          <div className={`${styles.modalContent} max-w-md w-full rounded-2xl overflow-hidden ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-2xl`}>
            {/* Header */}
            <div className="p-5 bg-gradient-to-r from-green-600 to-emerald-600 text-white text-center">
              <div className="text-4xl mb-2">🏆</div>
              <h3 className="text-xl font-bold">{gameTexts?.progress_title || 'My Progress'}</h3>
            </div>

            <div className="p-5">
              {/* Stats Grid */}
              <div className="grid grid-cols-2 gap-3 mb-5">
                <div className={`p-4 rounded-xl text-center ${isDarkMode ? 'bg-gray-700' : 'bg-orange-50'}`}>
                  <p className="text-3xl font-black text-orange-500">
                    {userState?.recipes_completed?.length || 0}
                  </p>
                  <p className={`text-xs font-medium mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{gameTexts?.progress_recipes_cooked || 'Recipes Cooked'}</p>
                </div>
                <div className={`p-4 rounded-xl text-center ${isDarkMode ? 'bg-gray-700' : 'bg-green-50'}`}>
                  <p className="text-3xl font-black text-green-500">
                    {(userState?.total_saved_dh || 0).toFixed(1)}
                  </p>
                  <p className={`text-xs font-medium mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{gameTexts?.progress_dh_saved || 'DH Saved'}</p>
                </div>
                <div className={`p-4 rounded-xl text-center ${isDarkMode ? 'bg-gray-700' : 'bg-blue-50'}`}>
                  <p className="text-3xl font-black text-blue-500">
                    {userState?.bargain_success_count || 0}
                  </p>
                  <p className={`text-xs font-medium mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{gameTexts?.progress_bargains_won || 'Bargains Won'}</p>
                </div>
                <div className={`p-4 rounded-xl text-center ${isDarkMode ? 'bg-gray-700' : 'bg-red-50'}`}>
                  <p className="text-3xl font-black text-red-500">
                    {(userState?.total_spent_dh || 0).toFixed(1)}
                  </p>
                  <p className={`text-xs font-medium mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{gameTexts?.progress_dh_spent || 'DH Spent'}</p>
                </div>
              </div>

              {/* Recipes Cooked List */}
              <div className="mb-4">
                <h4 className={`font-bold text-sm mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  {gameTexts?.progress_recipes_list_title || "Recipes You've Cooked"}
                </h4>
                {userState?.recipes_completed?.length > 0 ? (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {userState.recipes_completed.map((recipeId, index) => {
                      const recipe = recipes.find(r => r.id === recipeId);
                      return (
                        <div
                          key={index}
                          className={`flex items-center gap-3 p-3 rounded-lg ${isDarkMode ? 'bg-gray-700/50' : 'bg-gray-50'}`}
                        >
                          <div className={`w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 flex items-center justify-center text-lg ${isDarkMode ? 'bg-gray-600' : 'bg-orange-100'}`}>
                            {recipe?.image_url ? (
                              <img src={recipe.image_url} alt="" className="w-full h-full object-cover" />
                            ) : '🍲'}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-bold text-sm">{recipe?.name_english || 'Unknown Recipe'}</p>
                            <p className="text-xs text-blue-500">{recipe?.name_darija || ''}</p>
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteConfirm({ recipeId, recipeName: recipe?.name_english || 'Unknown Recipe' });
                            }}
                            className={`text-xs px-2 py-1 rounded-lg flex-shrink-0 transition-all ${isDarkMode ? 'text-red-400 hover:bg-red-900/30' : 'text-red-500 hover:bg-red-50'}`}
                            title="Remove from progress"
                          >
                            🗑️
                          </button>
                          <span className="text-green-500 text-lg flex-shrink-0">✓</span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className={`text-center py-6 rounded-lg ${isDarkMode ? 'bg-gray-700/30' : 'bg-gray-50'}`}>
                    <div className="text-3xl mb-2">🍳</div>
                    <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {gameTexts?.progress_no_recipes || "You haven't cooked any recipes yet!"}
                    </p>
                    <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      {gameTexts?.progress_no_recipes_hint || 'Pick a recipe above to start your journey.'}
                    </p>
                  </div>
                )}
              </div>

              {/* Bargaining skill */}
              {userState?.bargain_success_count > 0 && (
                <div className={`p-3 rounded-xl mb-4 ${isDarkMode ? 'bg-amber-900/30 border border-amber-800' : 'bg-amber-50 border border-amber-200'}`}>
                  <p className={`text-xs font-bold ${isDarkMode ? 'text-amber-300' : 'text-amber-700'}`}>
                    💰 {gameTexts?.progress_bargain_skill || 'Bargaining Skill'}
                  </p>
                  <p className={`text-xs mt-1 ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`}>
                    {userState.bargain_success_count >= 20 ? (gameTexts?.progress_skill_master || 'Master Bargainer! Merchants fear you.') :
                     userState.bargain_success_count >= 10 ? (gameTexts?.progress_skill_skilled || 'Skilled Haggler — you know your way around the souk.') :
                     userState.bargain_success_count >= 5 ? (gameTexts?.progress_skill_getting_better || 'Getting better! Keep bargaining.') :
                     (gameTexts?.progress_skill_beginner || 'Beginner — practice makes perfect!')}
                  </p>
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={() => setShowResetConfirm(true)}
                  className="flex-1 px-5 py-3 bg-red-500 text-white rounded-xl font-bold text-sm hover:bg-red-600 transition-all"
                >
                  🔄 Reset All
                </button>
                <button
                  onClick={() => setShowProgressModal(false)}
                  className="flex-1 px-5 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl font-bold text-sm hover:shadow-lg transition-all"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===== DELETE CONFIRMATION MODAL ===== */}
      {/* ===== RESET ALL PROGRESS CONFIRMATION ===== */}
      {showResetConfirm && (
        <div className={styles.modalOverlay} style={{ zIndex: 60 }} onClick={() => setShowResetConfirm(false)}>
          <div className={`${styles.modalContent} max-w-sm w-full p-6 rounded-2xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-2xl`} onClick={e => e.stopPropagation()}>
            <div className="text-center">
              <div className="text-5xl mb-3">⚠️</div>
              <h3 className="text-xl font-bold mb-2 text-red-500">Reset All Progress?</h3>
              <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                This will permanently delete all your cooking progress, including:
              </p>
              <ul className={`text-sm text-left mb-5 space-y-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                <li>• All cooked recipes</li>
                <li>• DH Saved & DH Spent stats</li>
                <li>• Bargaining stats</li>
                <li>• Active shopping session</li>
              </ul>
              <p className="text-xs text-red-400 font-bold mb-4">This action cannot be undone!</p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowResetConfirm(false)}
                  className={`flex-1 py-3 rounded-xl font-bold text-sm ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'} transition-all`}
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    try {
                      const res = await fetch(`${API}/souk/reset-progress/${userId}`, {
                        method: 'POST',
                        headers: { 'Authorization': `Bearer ${userToken}` }
                      });
                      if (res.ok) {
                        const data = await res.json();
                        setUserState(data);
                        setActiveSession(null);
                      }
                    } catch (err) { console.error('Reset error:', err); }
                    setShowResetConfirm(false);
                  }}
                  className="flex-1 py-3 rounded-xl font-bold text-sm bg-red-500 text-white hover:bg-red-600 transition-all"
                >
                  Reset Everything
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {deleteConfirm && (
        <div
          className={styles.modalOverlay}
          style={{ zIndex: 60 }}
          onClick={() => setDeleteConfirm(null)}
        >
          <div
            className={`${styles.modalContent} w-[90vw] max-w-sm`}
            onClick={e => e.stopPropagation()}
            style={{ animation: 'scaleIn 0.25s ease-out' }}
          >
            <div className={`rounded-t-2xl p-5 text-center ${isDarkMode ? 'bg-red-900/40' : 'bg-red-50'}`}>
              <div className="text-4xl mb-2">🗑️</div>
              <h3 className={`font-bold text-lg ${isDarkMode ? 'text-red-300' : 'text-red-700'}`}>
                {gameTexts?.delete_confirm_title || 'Remove Recipe?'}
              </h3>
            </div>
            <div className={`p-5 text-center ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
              <p className={`text-sm mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                {gameTexts?.delete_confirm_text || 'Are you sure you want to remove'}
              </p>
              <p className={`font-bold text-base mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                "{deleteConfirm.recipeName}"
              </p>
              <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                {gameTexts?.delete_confirm_note || 'from your cooked recipes? Your bargaining stats will not change.'}
              </p>
            </div>
            <div className={`flex gap-3 p-4 rounded-b-2xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
              <button
                onClick={() => setDeleteConfirm(null)}
                className={`flex-1 py-3 rounded-xl font-bold text-sm transition-all ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  console.log('[SOUK DELETE] Removing recipe:', deleteConfirm.recipeId, 'for user:', userId);
                  try {
                    const res = await fetch(`${API}/souk/remove-completed-recipe`, {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                        ...(userToken ? { 'Authorization': `Bearer ${userToken}` } : {})
                      },
                      body: JSON.stringify({ user_id: userId, recipe_id: deleteConfirm.recipeId })
                    });
                    console.log('[SOUK DELETE] Response status:', res.status);
                    if (res.ok) {
                      const data = await res.json();
                      console.log('[SOUK DELETE] Server returned updated state:', data);
                      setUserState(data);
                    } else {
                      const errText = await res.text();
                      console.error('[SOUK DELETE] Failed:', res.status, errText);
                    }
                  } catch (err) {
                    console.error('[SOUK DELETE] Error:', err);
                  }
                  setDeleteConfirm(null);
                }}
                className="flex-1 py-3 rounded-xl font-bold text-sm bg-red-500 text-white hover:bg-red-600 transition-all"
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SoukBargainGame;
